import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
  UserPlus, Search, Trash2, Shield, User, Mail,
  X, Download, FileSpreadsheet, CheckSquare, Square,
  RefreshCw, ChevronDown, ChevronRight, Edit2, Link2, Copy, Check,
  Layers,
} from 'lucide-react';
import api from '../services/api.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { useDeviceDetect } from '../hooks/useDeviceDetect.js';
import toast from 'react-hot-toast';
import { PageSpinner } from '../components/Skeleton.jsx';
import Avatar from '../components/shared/Avatar.jsx';
import ModalCloseButton from '../components/shared/ModalCloseButton.jsx';

// ── Constants ─────────────────────────────────────────────────────────────────
const RATINGS = [
  { value: 1, label: 'Elite',      color: '#00ff88', emoji: '⭐' },
  { value: 2, label: 'Strong',     color: '#00d4ff', emoji: '🔵' },
  { value: 3, label: 'Average',    color: '#ff9500', emoji: '🟠' },
  { value: 4, label: 'Developing', color: '#ff4444', emoji: '🔴' },
  { value: 5, label: 'Beginner',   color: '#a78bfa', emoji: '🟣' },
];

// ── Classification system ─────────────────────────────────────────────────────
const CATEGORY_DEFS = [
  { key: 'player_full',  label: 'Player – Full Time', short: 'Player FT', emoji: '👤', color: '#00d4ff', isSpare: false, position: 'flex'   },
  { key: 'player_spare', label: 'Player Spare',        short: 'Spare',     emoji: '👥', color: '#ff9500', isSpare: true,  position: 'flex'   },
  { key: 'goalie_full',  label: 'Goalie – Full Time',  short: 'Goalie FT', emoji: '🥅', color: '#00e87a', isSpare: false, position: 'goalie' },
  { key: 'goalie_spare', label: 'Goalie Spare',         short: 'G Spare',   emoji: '🥅', color: '#7dd3b8', isSpare: true,  position: 'goalie' },
  { key: 'coaches',      label: 'Coaches',              short: 'Coach',     emoji: '🛡', color: '#ff8c1a', isSpare: false, position: null     },
];
const CAT_MAP = Object.fromEntries(CATEGORY_DEFS.map(c => [c.key, c]));

// PUT body for reclassify operations (coaches excluded — they have their own role)
const CLASSIFY_PUT = {
  player_full:  { position: 'flex',   isSpare: false, memberRole: 'player' },
  player_spare: { position: 'flex',   isSpare: true,  memberRole: 'player' },
  goalie_full:  { position: 'goalie', isSpare: false, memberRole: 'player' },
  goalie_spare: { position: 'goalie', isSpare: true,  memberRole: 'player' },
};

function classifyMember(m) {
  // Classify by player type (FT/Spare + skater/goalie) regardless of
  // member_role. Coaches and managers who also play are full team
  // members for game-day purposes — they show up in their respective
  // lists, with their staff role surfaced via a separate badge in the
  // row UI. Previous behavior put all coaches in a "Staff" bucket and
  // hid them from the Full-Time/Spare counts, which broke game
  // management when a coach also played.
  if (m.position === 'goalie') return m.isPrimary ? 'goalie_full' : 'goalie_spare';
  return m.isPrimary ? 'player_full' : 'player_spare';
}

const FILTER_TABS = [
  { key: 'all',          label: 'All' },
  { key: 'player_full',  label: '👤 FT Players'   },
  { key: 'player_spare', label: '👥 Spare Players' },
  { key: 'goalie_full',  label: '🥅 FT Goalies'   },
  { key: 'goalie_spare', label: '🥅 Spare Goalies' },
  { key: 'coaches',      label: '🛡 Coaches'         },
];

const getRating = r => RATINGS.find(x => x.value === r);

// 2026-09-17 (Phase 5) — "has a Game Day account" = a real member row
// (not a pending invite / unclaimed league slot / deleted account) who has
// logged in at least once. Everything invite-related (Re-send Invite, the
// personal sign-up link, the Resend Invites tool) only applies to the
// opposite: people with an email who have NOT set up an account yet.
const hasAccount = (m) => !!m && !m.isPending && !m.isDeleted && !m.isUnclaimed && !!m.lastLogin;
const needsInvite = (m) => !!m && !m.isDeleted && !['coach', 'manager'].includes(m.role)
  && !!(m.email || '').trim() && !m.missingEmail && !hasAccount(m);

// 2026-09-17 — label for the Copy Emails button by active filter chip.
const COPY_LABEL = {
  all:          'Copy Emails',
  player_full:  'Copy FT emails',
  player_spare: 'Copy spare emails',
  goalie_full:  'Copy FT goalie emails',
  goalie_spare: 'Copy spare goalie emails',
  coaches:      'Copy coach emails',
};
const POS_SHORT = { forward: 'F', defense: 'D', goalie: 'G', flex: 'Flex' };

// ── Bulk Classification Picker (floats above the action bar) ──────────────────
function ClassifyPicker({ onSelect, onClose }) {
  return (
    <div style={{
      // Anchor to right edge so the popover doesn't overflow on narrow screens.
      // See SeasonPicker for the same fix — both pickers live on the right side
      // of the bulk action bar.
      position: 'absolute', bottom: '100%', right: 0, zIndex: 60, marginBottom: 8,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 14, padding: 12, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
      minWidth: 'min(230px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
    }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
        Reclassify as…
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {CATEGORY_DEFS.filter(c => c.key !== 'coaches').map(c => (
          <button key={c.key} onClick={() => onSelect(c.key)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px',
            borderRadius: 9, textAlign: 'left', cursor: 'pointer',
            background: `${c.color}15`, border: `1.5px solid ${c.color}40`,
            color: c.color, transition: 'all 0.15s',
          }}>
            <span style={{ fontSize: 15 }}>{c.emoji}</span>
            <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>{c.label}</span>
          </button>
        ))}
      </div>
      <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: '5px 0', background: 'none', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
        Cancel
      </button>
    </div>
  );
}

// ── Bulk Season Picker (floats above the action bar) ─────────────────────────
// Lists available seasons so the coach can either:
//   (a) add selected players to a season (main row click), or
//   (b) remove selected players from a season they're already in (× button
//       on the right — only shown when at least one selected player is
//       currently in that season).
//
// `membershipCounts[seasonId]` is the number of currently-selected players
// already in that season; when > 0 the × action is enabled.
function SeasonPicker({ seasons, membershipCounts, onAdd, onRemove, onClose }) {
  return (
    <div style={{
      // Anchor to the RIGHT edge of the Season button so the popover opens
      // leftward into the visible area. Bug: on narrow mobile viewports with
      // left:0 the popover was extending past the right edge of the screen
      // and getting clipped.
      position: 'absolute', bottom: '100%', right: 0, zIndex: 60, marginBottom: 8,
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 14, padding: 12, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
      // Cap minWidth so it can't force horizontal overflow on narrow screens;
      // use calc to stay inside the viewport with a bit of breathing room.
      minWidth: 'min(280px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      width: 320,
      maxHeight: 340, overflowY: 'auto',
    }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>
        Add to or remove from season…
      </p>
      {seasons.filter(s => s.isActive !== false).length === 0 ? (
        <p style={{ fontSize: 12, color: 'var(--text-2)', padding: '8px 4px' }}>
          No active seasons. Create one from the Seasons page (or restore an archived season).
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Apr 2026 — only ACTIVE seasons offered for bulk-add.
              Archived seasons stay out of forward-action pickers so
              coaches don't accidentally re-fill a wrapped-up season. */}
          {seasons.filter(s => s.isActive !== false).map(s => {
            const inSeasonCount = membershipCounts[s.id] || 0;
            return (
              <div key={s.id} style={{
                display: 'flex', alignItems: 'stretch', gap: 4,
              }}>
                {/* Add button — main clickable area */}
                <button onClick={() => onAdd(s)} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 8, padding: '9px 11px', borderRadius: 9, textAlign: 'left',
                  background: 'rgba(0,212,255,0.08)', border: '1.5px solid rgba(0,212,255,0.3)',
                  color: 'var(--ice)', cursor: 'pointer', flex: 1, minWidth: 0,
                }}
                title={`Add selected to ${s.name}`}>
                  <span style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.name}
                  </span>
                  {(s.memberCount ?? 0) > 0 && (
                    <span style={{ fontSize: 10, color: 'var(--text-2)', flexShrink: 0 }}>
                      {s.memberCount} in player list
                    </span>
                  )}
                </button>
                {/* Remove button — only active when selection has members in this season */}
                {inSeasonCount > 0 && (
                  <button
                    onClick={() => onRemove(s)}
                    title={`Remove ${inSeasonCount} selected from ${s.name}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      gap: 3, padding: '0 10px', borderRadius: 9,
                      background: 'rgba(255,59,59,0.1)', border: '1.5px solid rgba(255,59,59,0.3)',
                      color: '#ff6b6b', cursor: 'pointer', flexShrink: 0,
                      fontSize: 11, fontWeight: 700, minWidth: 52,
                    }}>
                    <X size={11} strokeWidth={2.5} /> {inSeasonCount}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: '5px 0', background: 'none', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
        Cancel
      </button>
    </div>
  );
}

// ── Welcome-status banner (2026-08-08, per Gilles) ────────────────────────────
// Coach-only. Answers "who was actually EMAILED about this team?" for members
// who already had Game Day accounts — the group the old bulk add/copy flows
// added silently. Shows an amber banner when anyone was never told, lists the
// names, and offers a one-click backfill send (server recomputes the target
// set, so double-clicks can't double-email). Hidden entirely once everyone
// has been welcomed — zero noise on healthy teams.
function WelcomeStatusBanner({ teamId }) {
  const [status,   setStatus]   = useState(null);   // { members, notWelcomedCount }
  const [expanded, setExpanded] = useState(false);
  const [sending,  setSending]  = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/members/welcome-status`);
      setStatus(data);
    } catch { /* banner is best-effort — silent on error / pre-deploy backend */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [teamId]);

  if (!status || status.notWelcomedCount === 0) return null;
  const missing = status.members.filter(m => !m.welcomed && m.isActive);

  const sendWelcome = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/members/send-welcome`);
      toast.success(data.message || `Sent ${data.sent} welcome emails`);
      load(); // refresh — the banner disappears once everyone is covered
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send welcome emails');
    }
    setSending(false);
  };

  return (
    <div className="mb-4 p-4 rounded-xl"
      style={{ background: 'rgba(255,149,0,0.07)', border: '1px solid rgba(255,149,0,0.30)' }}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1" style={{ minWidth: 220 }}>
          <p className="text-sm font-bold" style={{ color: 'var(--puck)' }}>
            📭 {status.notWelcomedCount} player{status.notWelcomedCount !== 1 ? 's' : ''} never got an email about this team
          </p>
          <p className="text-xs mt-1" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
            They already had Game Day accounts, so adding them didn't send an invite.
            Send them a "you've been added to the team" email now.
            <button
              onClick={() => setExpanded(v => !v)}
              style={{ background: 'none', border: 'none', color: 'var(--ice)', cursor: 'pointer', fontSize: 'inherit', padding: 0, marginLeft: 6, textDecoration: 'underline' }}>
              {expanded ? 'Hide names' : 'See who'}
            </button>
          </p>
          {expanded && (
            <p className="text-xs mt-2" style={{ color: 'var(--text)', lineHeight: 1.6 }}>
              {missing.map(m => m.name).join(', ')}
            </p>
          )}
        </div>
        <button onClick={sendWelcome} disabled={sending}
          className="btn-ice text-xs px-4 py-2 flex-shrink-0">
          {sending
            ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Sending…</>
            : <><Mail size={13} /> Email them now</>}
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function PlayersPage() {
  const { teamId }               = useParams();
  // v2.5.187 — shared current-season selection scopes the roster. null = All.
  const { currentSeasonId = null, setCurrentSeason } = useOutletContext() || {};
  const navigate                 = useNavigate();
  const { isCoach, loading: rL } = useTeamRole(teamId);
  const { isMobilePhone } = useDeviceDetect();
  // Narrow shorthand — many layout decisions below branch on whether we're
  // on a phone vs. tablet/desktop. Cards need a tighter layout on phones
  // where the row would otherwise cram 7+ elements into ~380px.
  const isPhone = isMobilePhone;

  const [members,        setMembers]        = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [search,         setSearch]         = useState('');
  const [filter,         setFilter]         = useState('all');
  // Apr 30 2026 (App_Change_List #21) — additional filter axes.
  // Coach feedback: with 30+ players on a team, the search box +
  // category chips weren't enough. Added:
  //   • skillFilter — exact-match rating chip (1..5 + 'all')
  //   • sortBy       — toggle between first-name and last-name sort
  //   • seasonFilter — limit list to players in a specific season,
  //     or 'all' for unscoped. Plays nicely with the Schedules
  //     panel's per-row season list.
  // All four work together (intersected) — search AND skill AND
  // category AND season all have to match. sortBy applies to the
  // grouped output so each section stays alphabetically tidy.
  const [skillFilter,    setSkillFilter]    = useState('all'); // 'all' | '1' | '2' | '3' | '4' | '5'
  const [sortBy,         setSortBy]         = useState('first'); // 'first' | 'last'
  const [seasonFilter,   setSeasonFilter]   = useState(currentSeasonId || 'all'); // 'all' | <season_id>
  const [showAdd,        setShowAdd]        = useState(false);
  // Apr 2026 (Tom_Notes B15) — placeholder-add modal state.
  // Separate from showAdd because it has different fields and a
  // different submit endpoint. Coach picks: name, role, position,
  // optional rating, optional notes.
  const [showAddPlaceholder, setShowAddPlaceholder] = useState(false);
  const [placeholderForm, setPlaceholderForm] = useState({
    name: '', role: 'player', position: 'flex', rating: '',
    isSpare: false, notes: '',
  });
  const [savingPlaceholder, setSavingPlaceholder] = useState(false);
  const [editId,         setEditId]         = useState(null);
  const [editForm,       setEditForm]       = useState({});
  const [inviteMsg,      setInviteMsg]      = useState('');
  const [addForm,        setAddForm]        = useState({ name: '', email: '', memberRole: 'player', isSpare: false, position: 'flex', noEmail: false });
  // Season assignment inside the Add Player modal. Coach ticks one or more
  // seasons and the invite endpoint attaches them in one request. An empty
  // array means "add to team only, no season" — which triggers a
  // confirmation step so the coach doesn't forget unintentionally.
  const [addSeasonIds,   setAddSeasonIds]   = useState([]);
  const [addNoSeasonConfirm, setAddNoSeasonConfirm] = useState(false);
  // 2026-07-10 (Phase 3.1) — the blue Add button's individual/bulk menu.
  const [showAddMenu, setShowAddMenu] = useState(false);
  // 2026-07-12 bugfix — the Phase 3.2 auto-attach effect that used to live
  // here was reading `seasons` in its dependency array before `seasons`
  // was declared (that useState is further down, near the other
  // season-picker state). A dependency array is evaluated immediately as
  // part of calling useEffect(), unlike the effect body itself, so this
  // threw "Cannot access 'seasons' before initialization" (a TDZ
  // ReferenceError) on EVERY render of this page, crashing the Players
  // route outright. Moved below the `seasons` state declaration — see
  // the "auto-attach active season" effect near the other season effects.
  const [aiLoading,      setAiLoading]      = useState(null);
  const [aiSuggestions,  setAiSuggestions]  = useState({});
  const [savingRating,   setSavingRating]   = useState(null);
  const [bulkSending,    setBulkSending]    = useState(false);
  // Per-row single-invite-send state (inline Send button for pending invites
  // without requiring the user to open the edit panel first). Value is the
  // member.id currently sending.
  const [sendingSingle,  setSendingSingle]  = useState(null);
  const [selected,       setSelected]       = useState(new Set());
  const [bulkPickerOpen, setBulkPickerOpen] = useState(false);
  // 2026-09-17 (Phase 5) — Resend Invites confirmation popup + new bulk pickers.
  const [resendModalOpen, setResendModalOpen] = useState(false);
  const [bulkRatingOpen,  setBulkRatingOpen]  = useState(false);
  const [bulkPosOpen,     setBulkPosOpen]     = useState(false);
  const [bulkWorking,    setBulkWorking]    = useState(false);
  // Per-player personal invite link (Apr 2026). Cached per member.id so
  // a coach can flip between players without re-fetching. The fetch is
  // lazy — we only call the backend when the coach clicks "Show
  // personal link" inside the edit panel. Pending invites surface the
  // existing token; real users get a fresh 30-day single-use token.
  const [personalLinks,  setPersonalLinks]  = useState({});  // { [memberId]: { link, type, expiresAt } }
  const [personalLinkLoading, setPersonalLinkLoading] = useState(null); // memberId currently fetching
  const [personalLinkCopied,  setPersonalLinkCopied]  = useState(null); // memberId where copy succeeded
  // Season picker for bulk "add to season" action
  const [seasons,          setSeasons]          = useState([]);
  const [seasonPickerOpen, setSeasonPickerOpen] = useState(false);
  // Individual (in-edit-panel) season assignment state. Tracks the season id
  // currently being added to, or the seasonMemberId currently being removed,
  // so we can disable the control and show wait cursors during the round trip.
  const [addingToSeason,      setAddingToSeason]      = useState(null);
  const [removingFromSeason,  setRemovingFromSeason]  = useState(null);
  // "Email Players" composer state — opens a modal to send a coach-authored
  // email broadcast. Group filter OR explicit user-id selection OR both (the
  // backend picks userIds if given, otherwise the group). Subject+body live
  // in the composer. Targets resolved client-side for preview counts; the
  // actual recipient list is re-computed server-side so we can't leak hidden
  // emails.
  const [msgOpen,      setMsgOpen]      = useState(false);
  const [msgGroup,     setMsgGroup]     = useState('all'); // all|full_time|spares|goalies|coaches|custom
  const [msgCustomIds, setMsgCustomIds] = useState([]); // only used when group === 'custom'
  const [msgSubject,   setMsgSubject]   = useState('');
  const [msgBody,      setMsgBody]      = useState('');
  const [msgSending,   setMsgSending]   = useState(false);
  // Apr 2026 (Tom_Notes B8) — channel routing for group messages.
  // Defaults to email only; coach can turn on push and/or in-app
  // for the same blast. Server-side dedupes per user when both
  // push and in_app are set.
  const [msgChannels,  setMsgChannels]  = useState(['email']);
  const [collapsed,      setCollapsed]      = useState(new Set());
  const [removeConfirmId, setRemoveConfirmId] = useState(null); // member.id awaiting confirm
  const [sendingInvites, setSendingInvites] = useState(false);

  useEffect(() => { if (!rL) loadMembers(); }, [teamId, isCoach, rL]);
  // v2.5.187 — keep local filter in lockstep with shared current season.
  useEffect(() => { setSeasonFilter(currentSeasonId || 'all'); }, [currentSeasonId]);
  useEffect(() => { if (isCoach && teamId) loadInviteLink(); }, [teamId, isCoach]);
  // Load seasons so the bulk-action bar's season picker has something to show.
  // Only coaches get the bulk bar, so skip the fetch for non-coaches.
  useEffect(() => {
    if (!isCoach || !teamId) return;
    (async () => {
      try {
        const { data } = await api.get(`/teams/${teamId}/seasons`);
        setSeasons(data || []);
      } catch { /* non-fatal — picker will show "No seasons yet" */ }
    })();
  }, [teamId, isCoach]);
  // 2026-07-10 (Phase 3.2, coach request: "it should automatically know
  // which season the current page is on") — auto-attach manual adds to
  // the ACTIVE season instead of making the coach pick every time. The
  // chips stay available to change/extend; the explicit "No schedule"
  // chip suppresses this default. Deselecting everything re-defaults to
  // the active season (empty was never a valid submit state anyway).
  // 2026-07-12 — relocated below the `seasons` state (was above it,
  // causing a TDZ crash — see the bugfix note near showAddMenu above).
  useEffect(() => {
    if (addSeasonIds.length === 0 && !addNoSeasonConfirm) {
      const active = (seasons || []).find(s => s.isActive === true);
      if (active) setAddSeasonIds([active.id]);
    }
  }, [seasons, addSeasonIds, addNoSeasonConfirm]);

  const loadMembers = async () => {
    setLoading(true);
    try {
      // Pending invites are now merged into the members response by the backend
      const { data } = await api.get(`/teams/${teamId}/members`);
      setMembers(data);
    } catch { toast.error('Failed to load players'); }
    setLoading(false);
  };

  // Pending invite members that haven't had emails sent yet
  const unsentPending = useMemo(() =>
    // `inviteSent` is `false` only for coaches viewing pending invites
    // that haven't been sent yet. For non-coaches the field is undefined
    // (the backend deliberately strips this admin-only metadata). We
    // require an explicit `false` so non-coaches don't count every
    // pending invite as "unsent" — that's the bug that produced the
    // misleading "39 invites not yet sent" line on player views.
    // 2026-09-17 (per Gilles) — only rows WITH an email count: a spare
    // added without an address can't be invited, so it must not light the
    // amber "N pending invites — tap to send" banner.
    members.filter(m => m.isPending && m.inviteSent === false && !!(m.email || '').trim() && !m.missingEmail),
    [members]
  );

  // One-click send invites for all unsent pending players. Players with no
  // email on file are filtered out client-side (the backend would skip them
  // anyway) and surfaced in a separate warning toast so the coach knows
  // exactly who was skipped and why.
  const sendPendingInvites = async () => {
    // Partition the pending batch into "has email" and "no email" groups so
    // we can show a specific error for the latter, named.
    const withEmail    = unsentPending.filter(m =>  m.email && !m.missingEmail && m.inviteId);
    const withoutEmail = unsentPending.filter(m => !m.email ||  m.missingEmail);

    if (withEmail.length === 0 && withoutEmail.length === 0) return;

    // Warn up front about email-less players — don't block sending the rest
    if (withoutEmail.length > 0) {
      const names = withoutEmail.map(m => m.name || 'Unnamed player');
      const namesStr = names.slice(0, 5).join(', ') + (names.length > 5 ? ` and ${names.length - 5} more` : '');
      toast.error(
        `${withoutEmail.length} player${withoutEmail.length !== 1 ? 's' : ''} skipped — no email on file: ${namesStr}. Add an email address from their row to invite them.`,
        { duration: 8000 }
      );
    }

    if (withEmail.length === 0) return;

    const ids = withEmail.map(m => m.inviteId);
    setSendingInvites(true);
    try {
      const { data } = await api.post(`/teams/${teamId}/import-players/send-invites`, { inviteIds: ids });
      if (data.sent > 0) {
        toast.success(`📧 ${data.sent} invite${data.sent !== 1 ? 's' : ''} sent!`);
      }
      // Upgraded server response: use per-invite reasons to show a specific
      // failure message naming the affected players (e.g. email server rejected).
      if (Array.isArray(data.sendErrors) && data.sendErrors.length > 0) {
        const names = data.sendErrors.map(e => e.name).slice(0, 5).join(', ');
        const more = data.sendErrors.length > 5 ? ` and ${data.sendErrors.length - 5} more` : '';
        toast.error(`Email delivery failed for ${data.sendErrors.length}: ${names}${more}. They may have invalid addresses — check and resend.`, { duration: 8000 });
      } else if (data.failed > 0) {
        // Server reports failures without reasons (older backend) — generic message
        toast.error(`${data.failed} failed to send`);
      }
      loadMembers();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send invites'); }
    setSendingInvites(false);
  };

  // ── Filtering + grouping ──────────────────────────────────────────────────
  // Apr 30 2026 (App_Change_List #21) — extended to also intersect on
  // skillFilter and seasonFilter. Each predicate short-circuits early
  // for cheap rejection. Sort applied AFTER grouping so each
  // category section is independently sorted.
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return members.filter(m => {
      // Free-text name/email search.
      const ok = !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q);
      if (!ok) return false;
      // Skill filter — exact rating match. Coaches/managers without
      // a rating are excluded when a rating chip is selected.
      if (skillFilter !== 'all') {
        const r = parseInt(skillFilter, 10);
        if (m.rating !== r) return false;
      }
      // Season filter — match if the player has a seasonMember in the
      // selected season (member.seasons is the array we already
      // hydrate elsewhere). Players with no season memberships are
      // excluded when a specific season is chosen.
      if (seasonFilter !== 'all') {
        const seasonsList = m.seasons || [];
        if (!seasonsList.some(s => s.id === seasonFilter)) return false;
      }
      // Category filter (existing).
      if (filter === 'all') return true;
      // "Manager" chip filters by member_role (coach/manager), since
      // classifyMember now buckets those roles by their player type
      // (FT/Spare/Goalie) rather than a separate 'coaches' bucket.
      if (filter === 'coaches') return ['coach', 'manager'].includes(m.role);
      return classifyMember(m) === filter;
    });
  }, [members, search, filter, skillFilter, seasonFilter]);

  // ── Sort comparator ───────────────────────────────────────────────────────
  // First-name sort = alpha by display name.
  // Last-name sort = alpha by surname (last whitespace-separated token).
  // Falls back to display name when only one token exists.
  const sortMembers = (arr) => {
    const lastName = (n) => {
      if (!n) return '';
      const parts = String(n).trim().split(/\s+/);
      return (parts[parts.length - 1] || '').toLowerCase();
    };
    const firstName = (n) => String(n || '').trim().toLowerCase();
    return [...arr].sort((a, b) => {
      const ka = sortBy === 'last' ? lastName(a.name) : firstName(a.name);
      const kb = sortBy === 'last' ? lastName(b.name) : firstName(b.name);
      if (ka < kb) return -1;
      if (ka > kb) return 1;
      // Stable tiebreaker — fall back to firstName to keep e.g. two
      // "Smith"s in a deterministic order.
      const fa = firstName(a.name);
      const fb = firstName(b.name);
      return fa < fb ? -1 : fa > fb ? 1 : 0;
    });
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const c of CATEGORY_DEFS) g[c.key] = [];
    for (const m of filtered) {
      // When the Manager chip is active, group coach/manager rows under
      // the 'coaches' section header so the user sees them distinctly
      // instead of being scattered across their player classifications.
      // For all other filters, classify by player type (FT/Spare/Goalie)
      // so coach-players appear in their respective lists.
      if (filter === 'coaches' && ['coach','manager'].includes(m.role)) {
        g.coaches.push(m);
      } else {
        g[classifyMember(m)].push(m);
      }
    }
    // Apr 30 2026 (App_Change_List #21) — sort each category section.
    for (const k of Object.keys(g)) g[k] = sortMembers(g[k]);
    return g;
  }, [filtered, filter, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  // IDs that can be bulk-selected. Coaches/managers can be selected too
  // (they're on the player list for game management), but the backend
  // still blocks deleting the last remaining coach so teams can't be
  // orphaned. A coach selecting themselves for bulk delete would hit
  // that 400 from the server.
  const selectableIds = useMemo(() =>
    new Set(filtered.map(m => m.id)),
    [filtered]
  );
  const selectedVisible = useMemo(() => [...selected].filter(id => selectableIds.has(id)), [selected, selectableIds]);
  const selectedCount   = selectedVisible.length;

  const toggleSelect    = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection  = () => setSelected(new Set());
  const toggleSelectAll = () => selectedCount === selectableIds.size ? clearSelection() : setSelected(new Set(selectableIds));

  const toggleSelectCategory = catKey => {
    const catIds = new Set((grouped[catKey] || []).map(m => m.id));
    const allSel = [...catIds].every(id => selected.has(id));
    setSelected(prev => { const n = new Set(prev); catIds.forEach(id => allSel ? n.delete(id) : n.add(id)); return n; });
  };

  // ── Bulk actions ──────────────────────────────────────────────────────────
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const bulkDelete = async () => {
    const ids = selectedVisible;
    if (!ids.length) return;
    // First tap: show confirm state on the button; second tap: execute
    if (!bulkDeleteConfirm) { setBulkDeleteConfirm(true); return; }
    setBulkDeleteConfirm(false);
    setBulkWorking(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      const member = members.find(m => m.id === id);
      try {
        if (member?.isPending) {
          await api.delete(`/teams/${teamId}/invite/${member.inviteId}`);
        } else {
          await api.delete(`/teams/${teamId}/members/${id}`);
        }
        ok++;
      } catch { fail++; }
    }
    if (ok)   toast.success(`${ok} player${ok !== 1 ? 's' : ''} removed`);
    if (fail) toast.error(`${fail} could not be removed`);
    setSelected(new Set());
    setBulkWorking(false);
    loadMembers();
  };

  const bulkReclassify = async classKey => {
    const ids = selectedVisible;
    if (!ids.length) return;
    const putBody = CLASSIFY_PUT[classKey];
    if (!putBody) return;
    setBulkPickerOpen(false);
    setBulkWorking(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      const member = members.find(m => m.id === id);
      try {
        if (member?.isPending) {
          await api.patch(`/teams/${teamId}/invite/${member.inviteId}`, { classification: classKey });
        } else {
          // 2026-09-17 (per Gilles — "everyone became Flex" after a
          // transition): reclassifying FT ↔ Spare must NOT wipe a
          // coach-set Forward/Defence. Only send `position: 'flex'` when
          // the player is moving OFF goalie (goalie → skater); otherwise
          // keep whatever position/positions they already have.
          const body = { ...putBody };
          if (classKey.startsWith('player_') && member?.position !== 'goalie') delete body.position;
          await api.put(`/teams/${teamId}/members/${id}`, body);
        }
        ok++;
      } catch { fail++; }
    }
    const cat = CAT_MAP[classKey];
    if (ok)   toast.success(`${ok} player${ok !== 1 ? 's' : ''} → ${cat?.label}`);
    if (fail) toast.error(`${fail} failed to update`);
    setSelected(new Set());
    setBulkWorking(false);
    loadMembers();
  };

  // 2026-09-17 (Phase 5) — generic bulk field setter used by the Rating
  // picker, and a position setter (position + positions[] together, the
  // same pair the edit panel saves). Pending invites don't carry these
  // fields (their classification patch only knows FT/spare/goalie), so
  // they're skipped and counted.
  const bulkSetField = async (field, value, label) => {
    const ids = selectedVisible;
    if (!ids.length) return;
    setBulkRatingOpen(false);
    setBulkWorking(true);
    let ok = 0, fail = 0, skipped = 0;
    for (const id of ids) {
      const member = members.find(m => m.id === id);
      if (!member || member.isPending || member.isDeleted) { skipped++; continue; }
      try { await api.put(`/teams/${teamId}/members/${id}`, { [field]: value }); ok++; }
      catch { fail++; }
    }
    if (ok)      toast.success(`${ok} player${ok !== 1 ? 's' : ''} → ${label}`);
    if (skipped) toast(`${skipped} pending invite${skipped !== 1 ? 's' : ''} skipped (no account yet)`);
    if (fail)    toast.error(`${fail} failed to update`);
    setSelected(new Set());
    setBulkWorking(false);
    loadMembers();
  };
  const bulkSetPosition = async (p) => {
    const ids = selectedVisible;
    if (!ids.length) return;
    setBulkPosOpen(false);
    setBulkWorking(true);
    let ok = 0, fail = 0, skipped = 0;
    for (const id of ids) {
      const member = members.find(m => m.id === id);
      if (!member || member.isPending || member.isDeleted) { skipped++; continue; }
      try {
        await api.put(`/teams/${teamId}/members/${id}`, {
          position: p.key === 'flex' ? 'flex' : p.key,
          positions: p.positions,
        });
        ok++;
      } catch { fail++; }
    }
    if (ok)      toast.success(`${ok} player${ok !== 1 ? 's' : ''} → ${p.emoji} ${p.label}`);
    if (skipped) toast(`${skipped} pending invite${skipped !== 1 ? 's' : ''} skipped (no account yet)`);
    if (fail)    toast.error(`${fail} failed to update`);
    setSelected(new Set());
    setBulkWorking(false);
    loadMembers();
  };

  // Bulk-add selected players to a season. For each selected row, sends the
  // right request shape: `{ userId }` for real members, `{ inviteId }` for
  // pending invites. The backend endpoint is idempotent (ON CONFLICT DO NOTHING)
  // so re-adding an already-in-season player just succeeds silently.
  //
  // Runs requests in small parallel batches so we don't hammer the API
  // with 50+ concurrent connections on a large roster.
  const bulkAddToSeason = async (season) => {
    const ids = selectedVisible;
    if (!ids.length || !season) return;
    setSeasonPickerOpen(false);
    setBulkWorking(true);

    // Resolve each id into the backend payload shape
    const payloads = ids.map(id => {
      const member = members.find(m => m.id === id);
      if (!member) return null;
      if (member.isPending) {
        return member.inviteId ? { inviteId: member.inviteId } : null;
      }
      return member.userId ? { userId: member.userId } : null;
    }).filter(Boolean);

    let ok = 0;
    const failNames = [];
    const BATCH = 8;
    for (let i = 0; i < payloads.length; i += BATCH) {
      const batch = payloads.slice(i, i + BATCH);
      const results = await Promise.allSettled(batch.map(p =>
        api.post(`/teams/${teamId}/seasons/${season.id}/members`, p)
      ));
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') ok++;
        else {
          const payload = batch[idx];
          const m = members.find(x => (payload.userId && x.userId === payload.userId) || (payload.inviteId && x.inviteId === payload.inviteId));
          failNames.push(m?.name || m?.email || 'Unknown');
        }
      });
    }

    if (failNames.length === 0) {
      toast.success(`Added ${ok} player${ok !== 1 ? 's' : ''} to ${season.name}`);
    } else if (ok > 0) {
      toast.error(`Added ${ok}, but ${failNames.length} failed (e.g. ${failNames[0]})`, { duration: 5000 });
    } else {
      toast.error(`Failed to add any players (e.g. ${failNames[0]})`, { duration: 5000 });
    }

    setSelected(new Set());
    setBulkWorking(false);
    // Refresh members so the per-row seasons badges reflect the new assignments
    loadMembers();
    // Refresh seasons so the picker's "N in roster" counts update
    try {
      const { data } = await api.get(`/teams/${teamId}/seasons`);
      setSeasons(data || []);
    } catch { /* non-fatal */ }
  };

  // Bulk-remove selected players from a season. Only affects players who are
  // currently in that season — others in the selection are silently ignored
  // (nothing to remove for them, which is not an error). Uses each member's
  // `seasons[]` array to find the `seasonMemberId` needed for the DELETE.
  const bulkRemoveFromSeason = async (season) => {
    const ids = selectedVisible;
    if (!ids.length || !season) return;
    setSeasonPickerOpen(false);
    setBulkWorking(true);

    // Build the list of season_member rows to delete. A player might have
    // multiple `seasons` entries (one per season they're in) — we want the
    // one matching the target season's id.
    const seasonMemberIds = [];
    const affectedNames   = [];
    for (const id of ids) {
      const member = members.find(m => m.id === id);
      if (!member) continue;
      const link = (member.seasons || []).find(x => x.id === season.id);
      if (link?.seasonMemberId) {
        seasonMemberIds.push(link.seasonMemberId);
        affectedNames.push(member.name || member.email || 'Unknown');
      }
    }

    if (seasonMemberIds.length === 0) {
      toast(`None of the selected players are in ${season.name}`, { duration: 4000 });
      setBulkWorking(false);
      return;
    }

    let ok = 0;
    const failNames = [];
    const BATCH = 8;
    for (let i = 0; i < seasonMemberIds.length; i += BATCH) {
      const batch = seasonMemberIds.slice(i, i + BATCH);
      const batchNames = affectedNames.slice(i, i + BATCH);
      const results = await Promise.allSettled(batch.map(smId =>
        api.delete(`/teams/${teamId}/seasons/${season.id}/members/${smId}`)
      ));
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') ok++;
        else failNames.push(batchNames[idx]);
      });
    }

    if (failNames.length === 0) {
      toast.success(`Removed ${ok} player${ok !== 1 ? 's' : ''} from ${season.name}`);
    } else if (ok > 0) {
      toast.error(`Removed ${ok}, but ${failNames.length} failed (e.g. ${failNames[0]})`, { duration: 5000 });
    } else {
      toast.error(`Failed to remove any players (e.g. ${failNames[0]})`, { duration: 5000 });
    }

    setSelected(new Set());
    setBulkWorking(false);
    loadMembers();
    try {
      const { data } = await api.get(`/teams/${teamId}/seasons`);
      setSeasons(data || []);
    } catch { /* non-fatal */ }
  };

  // Add a single member to a season from inside their edit panel. Fires an
  // immediate API call (doesn't wait for Save) because season membership is
  // not part of the member PUT payload. On success, locally appends the new
  // season to the member's seasons array so the UI updates without a reload.
  const addMemberToSeason = async (member, season) => {
    if (!member || !season || addingToSeason) return;
    setAddingToSeason(season.id);
    const payload = member.isPending
      ? (member.inviteId ? { inviteId: member.inviteId } : null)
      : (member.userId ? { userId: member.userId } : null);
    if (!payload) {
      setAddingToSeason(null);
      toast.error('Cannot add this member — missing id');
      return;
    }
    try {
      await api.post(
        `/teams/${teamId}/seasons/${season.id}/members`, payload
      );
      toast.success(`Added to ${season.name}`);
      // Reload members to pick up the new season's seasonMemberId (needed for
      // later removal). The POST response doesn't include it, and guessing
      // would leave the × chip broken until a manual reload.
      loadMembers();
      api.get(`/teams/${teamId}/seasons`).then(r => setSeasons(r.data || [])).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to add to season';
      toast.error(msg);
    }
    setAddingToSeason(null);
  };

  // Remove a single member from a season from inside their edit panel.
  const removeMemberFromSeason = async (member, season) => {
    if (!member || !season?.seasonMemberId || removingFromSeason) return;
    setRemovingFromSeason(season.seasonMemberId);
    try {
      await api.delete(
        `/teams/${teamId}/seasons/${season.id}/members/${season.seasonMemberId}`
      );
      setMembers(prev => prev.map(m => m.id === member.id
        ? { ...m, seasons: (m.seasons || []).filter(s => s.seasonMemberId !== season.seasonMemberId) }
        : m
      ));
      toast.success(`Removed from ${season.name}`);
      api.get(`/teams/${teamId}/seasons`).then(r => setSeasons(r.data || [])).catch(() => {});
    } catch (err) {
      const msg = err?.response?.data?.error || 'Failed to remove from season';
      toast.error(msg);
    }
    setRemovingFromSeason(null);
  };

  // Count how many of the currently-selected players are in each season.
  // Feeds the picker's per-row remove button so it only shows when at least
  // one selected player is in that season.
  const seasonMembershipCounts = useMemo(() => {
    const counts = {};
    for (const id of selectedVisible) {
      const member = members.find(m => m.id === id);
      if (!member?.seasons) continue;
      for (const s of member.seasons) {
        if (!s.id) continue;
        counts[s.id] = (counts[s.id] || 0) + 1;
      }
    }
    return counts;
  }, [selectedVisible, members]);

  // ── Individual actions ────────────────────────────────────────────────────
  // Shared body for both "Add & Invite" and "Add Only" flows. Includes the
  // no-season confirmation gate: if the coach hasn't picked any seasons AND
  // the team has seasons to pick from, we show a confirm banner instead of
  // submitting. On the second click (confirm armed), we proceed.
  const runAddPlayer = async (e, { skipEmail }) => {
    if (e?.preventDefault) e.preventDefault();

    // v2.5.186 — "no email" path. The coach toggled "This person has no
    // email" in the Add Player modal: there's no account to invite, so
    // we create a placeholder roster slot instead (same as the old
    // "+ Slot" flow, now folded in here). Requires a name; email and the
    // season picker don't apply.
    if (addForm.noEmail) {
      const name = (addForm.name || '').trim();
      if (!name) { toast.error('Enter a name for this player'); return; }
      try {
        await api.post(`/teams/${teamId}/placeholders`, {
          name,
          role: addForm.memberRole || 'player',
          position: addForm.position || 'flex',
          rating: null,
          isPrimary: !addForm.isSpare,
          notes: '',
        });
        toast.success(`${name} added (no email — placeholder).`);
        setShowAdd(false);
        setAddForm({ name: '', email: '', memberRole: 'player', isSpare: false, position: 'flex', noEmail: false });
        setAddSeasonIds([]);
        setAddNoSeasonConfirm(false);
        setInviteMsg('');
        loadMembers();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Failed to add player');
      }
      return;
    }

    if (!addForm.email) return toast.error('Email is required');

    // Apr 2026: schedule pick is required when the team has schedules.
    // Previously we toasted a warning and let the second click through,
    // which coaches routinely missed. Now submit is blocked unless the
    // coach picked at least one schedule OR explicitly picked the
    // "No schedule — will assign later" chip.
    if (seasons.length > 0 && addSeasonIds.length === 0 && !addNoSeasonConfirm) {
      toast.error(
        'Pick a schedule (or "No schedule — will assign later") before adding.',
        { duration: 5000 }
      );
      return;
    }

    const payload = {
      ...addForm,
      name: addForm.name?.trim() || undefined,
      message: skipEmail ? undefined : (inviteMsg || undefined),
      seasonIds: addSeasonIds.length > 0 ? addSeasonIds : undefined,
      ...(skipEmail ? { skipEmail: true } : {}),
    };

    try {
      const { data } = await api.post(`/teams/${teamId}/invite`, payload);

      if (data.type === 'invited_email_failed') {
        // Surface the specific SendGrid reason (if any) so the coach can
        // distinguish a rejected-recipient vs. temporary-outage vs. config
        // issue. The row IS saved either way.
        const reason = data.sgFirstError
          ? ` Reason: ${data.sgFirstError}.`
          : '';
        toast(
          `⚠️ ${addForm.email} added to player list, but the invite email failed to send.${reason} ` +
          `Double-check the address or try again later.`,
          { icon: '⚠️', duration: 10000 }
        );
      } else if (data.type === 'invited_resent') {
        toast.success(`📧 Invite re-sent to ${addForm.email}`);
      } else {
        // Append a season-attach summary to the success toast so the coach
        // sees confirmation that BOTH the add AND the season assignment
        // worked in one operation.
        const seasonNote = data.seasonsAttached > 0
          ? ` · Added to ${data.seasonsAttached} season${data.seasonsAttached !== 1 ? 's' : ''}`
          : '';
        toast.success(`${data.message}${seasonNote}`);
      }
      setShowAdd(false);
      setAddForm({ name: '', email: '', memberRole: 'player', isSpare: false, position: 'flex', noEmail: false });
      setAddSeasonIds([]);
      setAddNoSeasonConfirm(false);
      setInviteMsg('');
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add player');
    }
  };

  const addPlayer     = (e) => runAddPlayer(e, { skipEmail: false });
  const addPlayerOnly = ()  => runAddPlayer(null, { skipEmail: true });

  // Apr 2026 (Tom_Notes B15) — submit handler for placeholder add.
  // Lighter-weight than the regular add: no email, no invite send,
  // no season assignment (placeholders don't have user accounts so
  // they can't have season memberships either). Coach gets back a
  // row id and we refresh the list.
  const addPlaceholder = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    const name = placeholderForm.name.trim();
    if (!name) return toast.error('Give the placeholder a name');
    setSavingPlaceholder(true);
    try {
      const ratingNum = placeholderForm.rating ? parseInt(placeholderForm.rating) : null;
      await api.post(`/teams/${teamId}/placeholders`, {
        name,
        role: placeholderForm.role,
        position: placeholderForm.position,
        // Apr 2026 — rating scale is 1-5 (Elite/Strong/Average/
        // Developing/Beginner). Previous bound was 1-4 here which
        // silently dropped Beginner (5) → null. The same bug was
        // fixed on the backend; frontend was missed.
        rating: ratingNum && ratingNum >= 1 && ratingNum <= 5 ? ratingNum : null,
        isPrimary: !placeholderForm.isSpare,
        notes: placeholderForm.notes,
      });
      toast.success('Placeholder added');
      setShowAddPlaceholder(false);
      setPlaceholderForm({ name: '', role: 'player', position: 'flex', rating: '', isSpare: false, notes: '' });
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add placeholder');
    }
    setSavingPlaceholder(false);
  };

  const [emailEditing,   setEmailEditing]   = useState(null);
  const [emailDraft,     setEmailDraft]     = useState('');
  const [emailSaving,    setEmailSaving]    = useState(false);
  const [emailStatus,    setEmailStatus]    = useState({});

  // Shareable join link
  const [inviteLink,     setInviteLink]     = useState(null);
  const [linkLoading,    setLinkLoading]    = useState(false);
  const [linkCopied,     setLinkCopied]     = useState(false);
  const [showLinkPanel,  setShowLinkPanel]  = useState(false);

  // Re-invite a real member who already has an account (sends via POST /invite)
  const [reInviting,     setReInviting]     = useState(null); // memberId

  // 2026-07-10 (Phase 4.2) — all active typed links (fulltime/spare/goalie
  // now coexist server-side; generating replaces only the same type).
  const [inviteLinks, setInviteLinks] = useState([]);
  const loadInviteLink = async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/invite/invite-link`);
      setInviteLink(data.link || null);
      setInviteLinks(data.links || []);
    } catch {}
  };

  const [linkClassification, setLinkClassification] = useState('player_full');
  // Season binding for the shareable link (Apr 2026). If set, anyone
  // who accepts the link auto-joins these seasons. Empty array = team
  // only, matching pre-existing behaviour.
  const [linkSeasonIds, setLinkSeasonIds] = useState([]);
  const generateInviteLink = async () => {
    setLinkLoading(true);
    try {
      // Map the chosen classification to the backend's role/position/spare
      // triple. Without this the backend defaulted to player_full for every
      // joiner — coaches reported new players coming in as full-timers even
      // when the link was meant for spares.
      const cls = CLASSIFY_PUT[linkClassification] || CLASSIFY_PUT.player_full;

      // May 4 2026 (v2.5.64) — auto-attach to the team's active season.
      // Coach feedback: "when I create the invite link, it should not be
      // asking me which season to add the players on, it should already
      // know which seasons we are on and identify that season."
      // Logic: find the season flagged isActive (mig 060 keeps this in
      // sync with teams.active_season_id). If found, attach. If the team
      // has no active season (rare — usually only true for brand-new
      // teams), no season is attached and joiners just hit the team
      // roster, same as before.
      const activeSeason = seasons.find(s => s.isActive === true);
      const autoSeasonIds = activeSeason ? [activeSeason.id] : [];

      const { data } = await api.post(`/teams/${teamId}/invite/invite-link`, {
        memberRole: cls.memberRole,
        position:   cls.position,
        isSpare:    cls.isSpare,
        seasonIds:  autoSeasonIds.length > 0 ? autoSeasonIds : undefined,
      });
      setInviteLink(data.link);
      loadInviteLink(); // 2026-07-10 — refresh the typed-links list too
      toast.success(activeSeason
        ? `Invite link created — joiners will auto-attach to ${activeSeason.name}!`
        : 'Invite link created!');
    } catch { toast.error('Failed to generate link'); }
    setLinkLoading(false);
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        // Fallback for older iOS/Android browsers without Clipboard API
        const el = document.createElement('textarea');
        el.value = inviteLink;
        el.style.cssText = 'position:fixed;opacity:0;top:0;left:0';
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    } catch {
      toast('Link copied — long-press if copy didn\'t work', { icon: '🔗' });
    }
  };

  const sendMemberInvite = async (member) => {
    setReInviting(member.id);
    try {
      const { data } = await api.post(`/teams/${teamId}/invite`, {
        email: member.email,
        memberRole: member.role || 'player',
        isSpare: !member.isPrimary,
      });
      toast.success(data.message);
    } catch (err) {
      const msg = err.response?.data?.error || '';
      // "Already on this team" means they're a member but need a password reset or
      // direct login — tell the coach clearly rather than showing a generic error
      if (msg.toLowerCase().includes('already')) {
        toast(`${member.name || member.email} is already on the team. If they can't log in, ask them to use "Forgot password" on the login page.`, { icon: 'ℹ️', duration: 6000 });
      } else {
        toast.error(msg || 'Failed to send invite');
      }
    }
    setReInviting(null);
  };

  const saveInviteEmail = async (member) => {
    if (!emailDraft.trim()) return;
    setEmailSaving(true);
    try {
      const { data } = await api.put(`/teams/${teamId}/invite/${member.inviteId}/email`, { email: emailDraft.trim() });
      // Show account status feedback
      setEmailStatus(prev => ({ ...prev, [member.inviteId]: { status: data.accountStatus, name: data.accountName } }));
      if (data.accountStatus === 'verified') {
        toast.success(`✅ ${data.accountName || emailDraft} already has a verified Game Day account!`);
      } else if (data.accountStatus === 'unverified') {
        toast(`⏳ ${emailDraft} has a Game Day account but hasn't verified their email yet.`);
      } else {
        toast.success('Email saved — send an invite when you\'re ready.');
      }
      setEmailEditing(null);
      setEmailDraft('');
      loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save email');
    }
    setEmailSaving(false);
  };

  // 2026-09-17 (per Gilles) — Resend Invites now opens a popup that lists
  // exactly who will get one (players with an email who have NOT set up a
  // Game Day account: pending invites AND real rows that never logged in)
  // and asks for confirmation. Tombstoned accounts and staff are excluded.
  const resendTargets = useMemo(() => members.filter(needsInvite), [members]);
  const bulkResendInvites = () => {
    if (!resendTargets.length) { toast('Everyone with an email already has a Game Day account'); return; }
    setResendModalOpen(true);
  };
  // Sends to an explicit list (the popup's list, or a bulk-selected subset).
  const sendInvitesTo = async (targets) => {
    setBulkSending(true);
    let sent = 0, failed = 0;
    const pendingIds = targets.filter(m => m.isPending && m.inviteId).map(m => m.inviteId);
    if (pendingIds.length) {
      try {
        await api.post(`/teams/${teamId}/import-players/send-invites`, { inviteIds: pendingIds });
        sent += pendingIds.length;
      } catch { failed += pendingIds.length; }
    }
    for (const m of targets.filter(m => !m.isPending)) {
      try { await api.post(`/teams/${teamId}/invite`, { email: m.email, memberRole: m.role || 'player', isSpare: !m.isPrimary }); sent++; }
      catch { failed++; }
    }
    if (sent)   toast.success(`Invite sent to ${sent} player${sent !== 1 ? 's' : ''}`);
    if (failed) toast.error(`${failed} invite${failed !== 1 ? 's' : ''} failed to send`);
    setBulkSending(false);
    setResendModalOpen(false);
    loadMembers();
  };

  // Send invite email for a single pending row — one-tap shortcut so coaches
  // don't have to open the edit panel just to send one invite. For pending
  // invites it calls import-players/send-invites with a single id; for real
  // members that haven't logged in it calls the team/invite endpoint.
  const sendSinglePending = async (member) => {
    if (!member) return;
    if (member.isDeleted) {
      // Tombstoned rows have a stored email but the user explicitly
      // deleted their account — re-inviting them would silently re-mail
      // someone who chose to leave. Refuse and tell the coach to remove
      // the row first if they want a clean slate.
      toast.error('This member deleted their account. Remove the row first if you want to invite someone new.');
      return;
    }
    setSendingSingle(member.id);
    try {
      if (member.isPending && member.inviteId) {
        await api.post(`/teams/${teamId}/import-players/send-invites`, { inviteIds: [member.inviteId] });
        toast.success(`Invite sent to ${member.email || member.name || 'player'}`);
      } else if (!member.isPending && member.email) {
        await api.post(`/teams/${teamId}/invite`, {
          email: member.email,
          memberRole: member.role || 'player',
          isSpare: !member.isPrimary,
        });
        toast.success(`Invite re-sent to ${member.email}`);
      }
      await loadMembers();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send invite');
    }
    setSendingSingle(null);
  };

  const saveEdit = async (member) => {
    try {
      if (member.isPending) {
        const isCoachOrManager = ['coach', 'manager'].includes(editForm.memberRole);

        if (isCoachOrManager) {
          // Coach/manager roles can't be derived from classification — send memberRole only
          await api.patch(`/teams/${teamId}/invite/${member.inviteId}`, {
            memberRole: editForm.memberRole,
            ...(editForm.rating != null ? { rating: editForm.rating } : {}),
          });
        } else {
          // For player roles, send classification (position + isSpare) plus optional rating
          const clsKey = editForm.position === 'goalie'
            ? (editForm.isSpare ? 'goalie_spare' : 'goalie_full')
            : (editForm.isSpare ? 'player_spare' : 'player_full');
          await api.patch(`/teams/${teamId}/invite/${member.inviteId}`, {
            classification: clsKey,
            ...(editForm.rating != null ? { rating: editForm.rating } : {}),
          });
        }
      } else {
        await api.put(`/teams/${teamId}/members/${member.id}`, {
          ...editForm,
          positions: editForm.positions || [],
        });
      }
      // May 4 2026 — preserve list order on edit save. Was calling
      // loadMembers() which re-fetched + replaced the whole array,
      // blowing away whatever order/sort the page had been showing
      // and visually "bumping" the edited row to the top of the
      // server-default order. Coach feedback: "when a player's
      // position is updated and saved, they are bumped to the top of
      // the list. Make it so they remain in the same sorted position."
      // Fix: patch the edited record in place. The toast confirmation
      // ("Player updated!") is unchanged. If a refresh is genuinely
      // needed the coach can pull-to-refresh / re-open the page.
      const updates = {
        ...editForm,
        positions: editForm.positions || [],
      };
      // `editForm.isSpare` only exists for non-pending edits and maps
      // to `isPrimary` on the row. Translate so the local row mirrors
      // what the server now has — without this, the chip and badge
      // computations would still show the pre-edit value until the
      // next full reload.
      if ('isSpare' in updates) {
        updates.isPrimary = !updates.isSpare;
        delete updates.isSpare;
      }
      // memberRole maps to `role` on the row.
      if ('memberRole' in updates) {
        updates.role = updates.memberRole;
        delete updates.memberRole;
      }
      setMembers(prev => prev.map(m => m.id === member.id
        ? { ...m, ...updates }
        : m
      ));
      toast.success('Player updated!');
      setEditId(null);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update player');
    }
  };

  const quickRating = async (member, rating) => {
    setSavingRating(member.id);
    try {
      if (member.isPending) {
        await api.patch(`/teams/${teamId}/invite/${member.inviteId}`, { rating });
      } else {
        await api.put(`/teams/${teamId}/members/${member.id}`, { rating });
      }
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, rating } : m));
    } catch { toast.error('Failed to update rating'); }
    setSavingRating(null);
  };

  const [removeFromSeasonConfirm, setRemoveFromSeasonConfirm] = useState(null); // `${memberId}:${seasonMemberId}`

  const removeFromSeason = async (member, season) => {
    const key = `${member.id}:${season.seasonMemberId}`;
    if (removeFromSeasonConfirm !== key) { setRemoveFromSeasonConfirm(key); return; }
    setRemoveFromSeasonConfirm(null);
    try {
      await api.delete(`/teams/${teamId}/seasons/${season.id}/members/${season.seasonMemberId}`);
      toast.success(`${member.name || 'Player'} removed from ${season.name}`);
      // Update local state — remove this season from the member's seasons array
      setMembers(prev => prev.map(m => m.id === member.id
        ? { ...m, seasons: m.seasons.filter(s => s.seasonMemberId !== season.seasonMemberId) }
        : m
      ));
    } catch { toast.error('Failed to remove from season'); }
  };

  const removeMember = async (member) => {
    // Two-tap confirm — avoids window.confirm which is broken on iOS Capacitor.
    // First call arms the confirm state, second call (within the timeout) fires
    // the actual delete. Auto-resets after 4s so a stray first tap doesn't leave
    // the button armed indefinitely.
    if (removeConfirmId !== member.id) {
      setRemoveConfirmId(member.id);
      // Auto-cancel so user doesn't have a "ticking bomb" if they walk away
      setTimeout(() => {
        setRemoveConfirmId(prev => prev === member.id ? null : prev);
      }, 4000);
      return;
    }
    setRemoveConfirmId(null);
    const label = member.name || member.email || 'this player';
    try {
      if (member.isPending) {
        await api.delete(`/teams/${teamId}/invite/${member.inviteId}`);
      } else {
        await api.delete(`/teams/${teamId}/members/${member.id}`);
      }
      toast.success(`${label} removed`);
      loadMembers();
    } catch { toast.error('Failed to remove player'); }
  };

  const requestAiRating = async member => {
    setAiLoading(member.id);
    try {
      const { data } = await api.post(`/teams/${teamId}/stats/ai/suggest-rating`, { memberId: member.id });
      if (data.suggestedRating) {
        setAiSuggestions(p => ({ ...p, [member.id]: { suggestedRating: data.suggestedRating, reason: data.reason } }));
        toast.success(`🤖 AI suggestion ready for ${member.name}`, { duration: 3000 });
      } else if (data.reason) {
        toast(data.reason.includes('not configured') ? '🤖 AI not configured — add an API key to enable rating suggestions' : `🤖 No rating change needed for ${member.name}`, { duration: 4000 });
      }
    } catch { toast.error('AI rating suggestion failed'); }
    setAiLoading(null);
  };

  const acceptAiRating = async member => {
    const s = aiSuggestions[member.id];
    if (!s) return;
    try {
      await api.put(`/teams/${teamId}/members/${member.id}`, { rating: s.suggestedRating });
      setAiSuggestions(p => { const n = { ...p }; delete n[member.id]; return n; });
      setMembers(prev => prev.map(m => m.id === member.id ? { ...m, rating: s.suggestedRating } : m));
      toast.success(`Rating updated to ${s.suggestedRating}`);
    } catch { toast.error('Failed to apply rating'); }
  };

  // Fetch / cache the per-player personal sign-up link. Lazy on click —
  // we don't pre-fetch for every roster row because most players won't
  // need it. Result is cached under personalLinks[member.id] so the
  // coach can flip between players without re-hitting the backend; a
  // refresh button (Rotate) clears the cache for that player.
  const fetchPersonalLink = async (member) => {
    if (personalLinkLoading) return;
    setPersonalLinkLoading(member.id);
    try {
      const { data } = await api.get(`/teams/${teamId}/members/${member.id}/personal-link`);
      setPersonalLinks(prev => ({ ...prev, [member.id]: data }));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate personal link');
    } finally {
      setPersonalLinkLoading(null);
    }
  };
  const copyPersonalLink = async (memberId) => {
    const entry = personalLinks[memberId];
    if (!entry?.link) return;
    try {
      await navigator.clipboard.writeText(entry.link);
      setPersonalLinkCopied(memberId);
      setTimeout(() => setPersonalLinkCopied(prev => prev === memberId ? null : prev), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — show the
      // browser prompt fallback so the user can still copy manually.
      window.prompt('Copy this link:', entry.link);
    }
  };

  // Split a full name into first + last. Best-effort: multi-word last names
  // go together (e.g. "Mary Van Dyke" → first "Mary", last "Van Dyke").
  const splitName = (fullName) => {
    const trimmed = (fullName || '').trim();
    if (!trimmed) return { first: '', last: '' };
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
  };

  const exportCSV = () => {
    const RL = { 1: 'Elite', 2: 'Strong', 3: 'Average', 4: 'Developing', 5: 'Beginner' };
    // Expanded column set: First Name + Last Name as separate columns (for
    // easy mailmerge in Excel/Outlook), Phone added, Seasons joined with "|".
    // Keeps the legacy columns so existing workflows don't break.
    const rows = [
      ['First Name', 'Last Name', 'Email', 'Phone', 'Classification', 'Position', 'Jersey #', 'Rating', 'Role', 'Seasons', 'Status'],
      ...members.map(m => {
        const cat = CAT_MAP[classifyMember(m)];
        const { first, last } = splitName(m.name);
        const seasonNames = (m.seasons || []).map(s => s.name).join(' | ');
        const status = m.isPending
          ? (m.inviteSent ? 'Invite Sent' : 'Invite Pending')
          : m.isDeleted
            ? 'Account Deleted'
            : (m.lastLogin ? 'Active' : 'Not Logged In');
        return [
          first, last,
          m.email || '',
          m.phone || '',
          cat?.label || '',
          m.position || '',
          m.jerseyNumber || '',
          RL[m.rating] || '',
          m.role || 'player',
          seasonNames,
          status,
        ];
      }),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    // Prepend UTF-8 BOM so Excel auto-detects UTF-8 for non-ASCII names
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `players-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Player list CSV downloaded');
  };

  // Collect every real (non-pending) member's email into a comma-separated
  // list ready to paste into Outlook/Gmail To/BCC. Skips blanks. Also includes
  // pending invites whose email has been set, since those people still need
  // to receive team emails. Duplicates are removed.
  const copyAllEmails = async () => {
    const seen = new Set();
    const emails = [];
    // 2026-09-17 (per Gilles) — copies whatever the page is currently
    // filtered to (category chip + search + season), not the whole roster.
    for (const m of filtered) {
      // Tombstoned members keep their email in the row for re-invite
      // purposes, but we never want to surface it in a copy-emails list
      // — coach pastes into Outlook and accidentally mails someone who
      // explicitly deleted their account.
      if (m.isDeleted) continue;
      const e = (m.email || '').trim();
      if (!e) continue;
      const key = e.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      emails.push(e);
    }
    if (emails.length === 0) {
      toast.error('No email addresses to copy');
      return;
    }
    const joined = emails.join(', ');
    try {
      await navigator.clipboard.writeText(joined);
      toast.success(`📋 Copied ${emails.length} ${filter === 'all' ? '' : (FILTER_TABS.find(t => t.key === filter)?.label.replace(/^[^ ]+ /, '') || '') + ' '}email${emails.length !== 1 ? 's' : ''} — paste into To/BCC`);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — fall back to prompt
      window.prompt('Copy this list:', joined);
    }
  };

  // Copy a single email to clipboard — used by the per-row copy button.
  const copySingleEmail = async (email) => {
    if (!email) return;
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`📋 ${email}`);
    } catch {
      window.prompt('Copy this email:', email);
    }
  };

  // ── Email Players ──────────────────────────────────────────────────────
  // Open the compose modal. Pre-selects group based on current filter chip,
  // or "custom" with the current selection when coaches have manually
  // picked players with the checkboxes.
  const openMessagePlayers = () => {
    if (selectedVisible.length > 0) {
      // Coach already ticked specific players — start in "custom" mode
      setMsgGroup('custom');
      const realSelected = selectedVisible
        .map(id => members.find(m => m.id === id))
        .filter(m => m?.userId)
        .map(m => m.userId);
      setMsgCustomIds(realSelected);
    } else {
      setMsgGroup('all');
      setMsgCustomIds([]);
    }
    setMsgSubject('');
    setMsgBody('');
    setMsgOpen(true);
  };

  // Live recipient count for the preview banner in the modal — purely
  // client-side, the backend computes the real list. A good sanity signal.
  const previewRecipientCount = useMemo(() => {
    if (!msgOpen) return 0;
    if (msgGroup === 'custom') return msgCustomIds.length;
    // Tombstoned (deleted-account) members are excluded from every group —
    // their email survives in the row but the user explicitly left, so
    // sending them mass messages is wrong on both privacy and UX grounds.
    // 2026-07-10 (batch-B C1, coach report) — pending invites are now
    // INCLUDED in every group (they have real emails; the backend group
    // resolver now emails team_invites too). Coaches labelled "Staff"
    // stays accounts-only (an invite is never staff). People without an
    // email (placeholders) drop out via `emailable`. Tombstones stay
    // excluded.
    const isFullTime = m => !m.isDeleted && !['coach','manager'].includes(m.role) && m.isPrimary && m.position !== 'goalie';
    const isSpare    = m => !m.isDeleted && !['coach','manager'].includes(m.role) && !m.isPrimary;
    const isGoalie   = m => !m.isDeleted && m.position === 'goalie';
    const isStaff    = m => !m.isPending && !m.isDeleted && ['coach','manager'].includes(m.role);
    const emailable  = m => !!m.email;
    let filter;
    switch (msgGroup) {
      case 'full_time': filter = isFullTime; break;
      case 'spares':    filter = isSpare;    break;
      case 'goalies':   filter = isGoalie;   break;
      case 'coaches':   filter = isStaff;    break;
      case 'all':
      default:          filter = m => !m.isDeleted; break;
    }
    return members.filter(m => filter(m) && emailable(m)).length;
  }, [msgOpen, msgGroup, msgCustomIds, members]);

  const sendMessagePlayers = async () => {
    if (!msgSubject.trim()) return toast.error('Subject is required');
    if (!msgBody.trim())    return toast.error('Message body is required');
    // Apr 2026 (Tom_Notes B8) — at least one channel.
    if (!Array.isArray(msgChannels) || msgChannels.length === 0) {
      return toast.error('Pick at least one channel (email, push, or in-app)');
    }

    setMsgSending(true);
    try {
      const payload = {
        subject: msgSubject.trim(),
        body:    msgBody.trim(),
        // Apr 2026 (Tom_Notes B8) — channels in payload. Backend
        // dispatches each channel independently and reports a
        // per-channel breakdown.
        channels: msgChannels,
      };
      if (msgGroup === 'custom') {
        if (msgCustomIds.length === 0) {
          setMsgSending(false);
          return toast.error('Select at least one player');
        }
        payload.userIds = msgCustomIds;
      } else {
        payload.group = msgGroup;
      }

      const { data } = await api.post(`/teams/${teamId}/messages/send`, payload);
      // Apr 2026 (Tom_Notes B8) — per-channel toast when backend
      // returns a breakdown; falls back to legacy single-count toast.
      const bd = data.breakdown || {};
      const parts = [];
      if (bd.emailSent != null && msgChannels.includes('email')) {
        parts.push(`${bd.emailSent} email${bd.emailSent !== 1 ? 's' : ''}`);
      }
      if (bd.pushSent != null && msgChannels.includes('push')) {
        parts.push(`${bd.pushSent} push`);
      }
      if (bd.inAppSent != null && msgChannels.includes('in_app')) {
        parts.push(`${bd.inAppSent} in-app`);
      }
      if (data.sent > 0 || parts.length > 0) {
        const summary = parts.length > 0
          ? `Sent: ${parts.join(' · ')}`
          : `Sent to ${data.sent ?? 0} of ${data.total ?? 0} recipient${(data.total ?? 0) !== 1 ? "s" : ""}`;
        toast.success(`📧 ${summary}`);
      } else {
        toast(
          '⚠️ No messages sent — no matching recipients (all opted out, or no email/push subscriptions).',
          { icon: '⚠️', duration: 6000 }
        );
      }
      setMsgOpen(false);
      setMsgSubject('');
      setMsgBody('');
      setMsgCustomIds([]);
      setMsgChannels(['email']);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send messages');
    }
    setMsgSending(false);
  };

  if (loading || rL) return <PageSpinner />;

  const realMembers = members.filter(m => !m.isPending);

  return (
    <div className="animate-fade-in" style={{ paddingBottom: selectedCount > 0 ? 110 : 16 }}>

      {/* 2026-08-08 — "who was never emailed about this team" banner +
          one-click backfill. Coach-only; renders nothing when everyone
          with an account has been welcomed. */}
      {isCoach && <WelcomeStatusBanner teamId={teamId} />}

      {/* ── Header ── */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h1 className={isPhone ? 'section-title text-3xl' : 'section-title text-4xl'}>Players</h1>
            <p className="text-secondary text-sm mt-0.5">
              {realMembers.filter(m => m.isPrimary && m.position !== 'goalie').length} skaters ·{' '}
              {realMembers.filter(m => m.position === 'goalie').length} goalies ·{' '}
              {realMembers.filter(m => !m.isPrimary && m.position !== 'goalie').length} spares
              {/* Coach-only: pending-invite admin metadata. Players don't
                  need to see this — it's the coach's reminder to send
                  invites they've drafted but not yet emailed. Showing it
                  on a player view also exposed a bug where the count was
                  wildly inflated because non-coaches don't get the
                  inviteSent boolean from the backend. ── */}
              {isCoach && unsentPending.length > 0 && (
                <span style={{ color: 'var(--puck)', marginLeft: 8 }}>
                  · {unsentPending.length} invite{unsentPending.length !== 1 ? 's' : ''} not yet sent
                </span>
              )}
            </p>
          </div>
          {isCoach && (
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Apr 2026 (Tom_Notes C1) — opening the Add modal now
                  pre-selects every active season. The coach can untick
                  any they don't want. Empty array still triggers the
                  "no season" confirmation step. The intent is to make
                  the most-common path (invite into the active season)
                  one click less. */}
              {/* 2026-07-10 (Phase 3.1, coach request) — the blue Add is the
                  single gateway for BOTH individual and bulk adds; the
                  separate toolbar "Import" button was removed. */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button onClick={() => setShowAddMenu(v => !v)} className="btn-ice">
                  <UserPlus size={15} /> Add <ChevronDown size={13} />
                </button>
                {showAddMenu && (
                  <div style={{
                    position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50,
                    background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                    minWidth: 250, overflow: 'hidden',
                  }}>
                    <button
                      className="w-full text-left px-4 py-3 text-sm flex items-center gap-2"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)' }}
                      onClick={() => {
                        setShowAddMenu(false);
                        const activeSeasonIds = seasons
                          .filter(s => s.isActive !== false)
                          .map(s => s.id);
                        setAddSeasonIds(activeSeasonIds);
                        setAddNoSeasonConfirm(false);
                        setShowAdd(true);
                      }}>
                      <UserPlus size={14} /> Add one player
                    </button>
                    <button
                      className="w-full text-left px-4 py-3 text-sm flex items-center gap-2"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)', borderTop: '1px solid var(--border)' }}
                      onClick={() => { setShowAddMenu(false); navigate(`/team/${teamId}/players/import`); }}>
                      <FileSpreadsheet size={14} /> Bulk import — PDF, screenshot, CSV
                    </button>
                  </div>
                )}
              </div>
              {/* v2.5.186 — "+ Slot" placeholder button removed at coach
                  request. Adding someone without an email is now handled
                  inside the regular Add Player modal via the "This person
                  has no email" toggle, so a separate placeholder button is
                  redundant. The placeholder modal + endpoint stay in the
                  code (unreferenced) in case the flow is brought back. */}
            </div>
          )}
        </div>

        {isCoach && (
          <div className="mt-3 space-y-2">
            {/* Horizontally scrollable tool strip — never wraps or overflows */}
            <div style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
                <button onClick={exportCSV} className="btn-ghost text-xs px-3 py-2 flex-shrink-0 whitespace-nowrap"><Download size={12} /> Export</button>
                <button onClick={copyAllEmails} className="btn-ghost text-xs px-3 py-2 flex-shrink-0 whitespace-nowrap"
                  title={filter === 'all'
                    ? "Copy every player's email (comma-separated) — paste into Outlook/Gmail To or BCC"
                    : `Copy the emails of the players currently shown (${FILTER_TABS.find(t => t.key === filter)?.label || 'filter'})`}>
                  <Copy size={12} /> {COPY_LABEL[filter] || 'Copy Emails'}
                </button>
                <button onClick={openMessagePlayers} className="btn-ghost text-xs px-3 py-2 flex-shrink-0 whitespace-nowrap"
                  title="Send an email to your players — replies come to your inbox"
                  style={{ color: 'var(--ice)', borderColor: 'var(--ice-a30)', background: 'var(--ice-a8)' }}>
                  <Mail size={12} /> Email Players
                </button>
                {/* 2026-07-10 (Phase 3.1) — Import button removed: bulk
                    import now lives under the blue Add menu. */}
                <button onClick={bulkResendInvites} disabled={bulkSending} className="btn-ghost text-xs px-3 py-2 flex-shrink-0 whitespace-nowrap"
                  title="Re-send the invite email to players who have an email but no Game Day account yet — you'll see the list first">
                  {bulkSending ? <><div className="spinner" style={{width:11,height:11}} /> Sending…</> : <><Mail size={12} /> Resend Invites{resendTargets.length ? ` (${resendTargets.length})` : ''}</>}
                </button>
                <button
                  onClick={() => { setShowLinkPanel(v => !v); }}
                  className="btn-ghost text-xs px-3 py-2 flex-shrink-0 whitespace-nowrap"
                  style={showLinkPanel ? { color: 'var(--ice)', borderColor: 'var(--ice-a30)', background: 'var(--ice-a8)' } : {}}>
                  <Link2 size={12} /> Invite Link
                </button>
                {/* 2026-09-17 (per Gilles) — Select all / Deselect all as a
                    side-by-side pair (was one toggling button). Individual
                    rows are picked with their own checkbox; a category
                    header's "Select group" button picks just that group. */}
                {selectableIds.size > 0 && (
                  <div className="flex flex-shrink-0 rounded-xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                    <button onClick={() => setSelected(new Set(selectableIds))}
                      disabled={selectedCount === selectableIds.size}
                      className="text-xs px-3 py-2 whitespace-nowrap flex items-center gap-1.5"
                      title="Select every player currently shown"
                      style={{ background: 'transparent', border: 'none', borderRight: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', opacity: selectedCount === selectableIds.size ? 0.5 : 1 }}>
                      <CheckSquare size={12} /> Select all
                    </button>
                    <button onClick={clearSelection}
                      disabled={selectedCount === 0}
                      className="text-xs px-3 py-2 whitespace-nowrap flex items-center gap-1.5"
                      title="Clear the selection"
                      style={{ background: 'transparent', border: 'none', color: selectedCount > 0 ? 'var(--ice)' : 'var(--text-2)', cursor: 'pointer', opacity: selectedCount === 0 ? 0.5 : 1 }}>
                      <Square size={12} /> Deselect all
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Shareable invite link panel */}
            {showLinkPanel && (
              <div className="mt-2 p-4 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text)' }}>
                  🔗 Shareable Join Link
                </p>
                <p className="text-xs mb-3" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                  Anyone with this link can join the team. Share via text, WhatsApp, or email — no invite required.
                </p>
                {/* Classification picker — coach chooses what new joiners
                    get assigned as. Without this, every shareable-link
                    joiner became a full-timer regardless of the coach's
                    intent. The picker is only shown BEFORE generating —
                    once a link exists the choice is baked in (Rotate to
                    change). New joiners can also override during
                    onboarding (their pick wins over the link's default
                    if they explicitly change it). ── */}
                {!inviteLink && (
                  <div className="mb-3">
                    <label className="text-xs font-semibold block mb-1.5" style={{ color: 'var(--text-2)' }}>
                      New joiners will be added as:
                    </label>
                    <select
                      className="input-field py-2"
                      value={linkClassification}
                      onChange={e => setLinkClassification(e.target.value)}
                      style={{ fontSize: 14 }}>
                      {CATEGORY_DEFS.filter(c => c.key !== 'coaches').map(c => (
                        <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
                      ))}
                    </select>
                    <p className="text-xs mt-1.5" style={{ color: 'var(--text-3)' }}>
                      Coaches can't be added through a shareable link — invite them directly by email.
                    </p>
                  </div>
                )}

                {/* May 4 2026 (v2.5.64) — coach feedback: the season
                    picker that used to live here was redundant, since
                    the team's active season is already known. The
                    picker has been replaced with a tiny info banner
                    confirming which season joiners will attach to. If
                    the team has no active season, joiners just hit the
                    team roster — same behavior as before, just
                    auto-detected instead of explicitly chosen.

                    Coaches who genuinely need to attach joiners to a
                    NON-active season (rare) can update the new player's
                    season membership from the Seasons page after they
                    join. The 99% case — "new joiners belong to the
                    season we're playing right now" — needs zero clicks. */}
                {!inviteLink && (() => {
                  const activeSeason = seasons.find(s => s.isActive === true);
                  if (!activeSeason) return null;
                  return (
                    <div className="mb-3 px-3 py-2 rounded-lg flex items-center gap-2"
                      style={{
                        background: 'rgba(0,212,255,0.06)',
                        border: '1px solid rgba(0,212,255,0.18)',
                      }}>
                      <span style={{ fontSize: 14 }}>🏒</span>
                      <p className="text-xs" style={{ color: 'var(--text-2)', lineHeight: 1.4 }}>
                        Joiners will be auto-attached to{' '}
                        <strong style={{ color: 'var(--ice)' }}>{activeSeason.name}</strong>
                        {' '}(your active season).
                      </p>
                    </div>
                  );
                })()}
                {inviteLink ? (<>
                  {/* URL display — full width, tap to select */}
                  <div className="mb-2 px-3 py-2 rounded-lg text-xs select-all break-all"
                    style={{ background: 'var(--surface-3)', border: '1px solid var(--border)', color: 'var(--text-2)', fontFamily: 'monospace', lineHeight: 1.6, cursor: 'text', WebkitUserSelect: 'all', userSelect: 'all' }}
                    onClick={e => { const r = document.createRange(); r.selectNodeContents(e.currentTarget); const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); }}>
                    {inviteLink}
                  </div>
                  {/* Actions row */}
                  <div className="flex gap-2">
                    <button onClick={copyLink}
                      className="btn-ice flex-1 justify-center py-2.5 text-sm flex items-center gap-2">
                      {linkCopied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Link</>}
                    </button>
                    <button onClick={() => {
                      // Clear the visible link so the classification +
                      // season-picker reappear. The coach can re-pick
                      // seasons (and classification) before clicking
                      // Generate again — clicking Generate will retire
                      // the old link server-side via the /invite-link
                      // POST's one-active-link-at-a-time logic. This
                      // avoids silently inheriting the old season set
                      // when the coach wanted a fresh binding. ──
                      setInviteLink(null);
                      setLinkCopied(false);
                    }} disabled={linkLoading}
                      className="btn-ghost px-4 py-2.5 text-sm flex items-center gap-2 flex-shrink-0"
                      title="Generate a fresh link — old link stops working immediately">
                      <RefreshCw size={14} />
                      <span className="hidden sm:inline">Refresh</span>
                    </button>
                  </div>
                  <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                    Tap Refresh to generate a fresh link. Only the link of the SAME type
                    (full-time / spare / goalie) is replaced — other types keep working.
                  </p>
                </>) : (
                  <button onClick={generateInviteLink} disabled={linkLoading}
                    className="btn-ice w-full justify-center py-3 flex items-center gap-2">
                    {linkLoading
                      ? <><div className="spinner" style={{width:13,height:13}} /> Generating…</>
                      : <><Link2 size={14} /> Generate Invite Link</>}
                  </button>
                )}

                {/* 2026-07-10 (Phase 4.2, coach request) — every ACTIVE link
                    by type, each with copy + delete. Types coexist now;
                    generating a link only replaces its own type. */}
                {inviteLinks.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-xs font-bold" style={{ letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                      Active invite links
                    </p>
                    {inviteLinks.map(l => (
                      <div key={l.token} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                        <span className="text-xs font-semibold flex-shrink-0" style={{ color: 'var(--ice)', minWidth: 76 }}>
                          {l.type === 'goalie' ? '🥅 Goalie' : l.type === 'spare' ? '🔄 Spare' : '👤 Full-time'}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-3)', fontFamily: 'monospace', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.link}
                        </span>
                        <button type="button" title="Copy link"
                          onClick={async () => {
                            try { await navigator.clipboard.writeText(l.link); toast.success(`${l.type} link copied`); }
                            catch { toast.error('Copy failed — long-press the link text instead'); }
                          }}
                          className="btn-ghost px-2 py-1 flex-shrink-0" style={{ fontSize: 11 }}>
                          <Copy size={12} />
                        </button>
                        <button type="button" title="Delete this link — it stops working immediately"
                          onClick={async () => {
                            if (!window.confirm(`Delete the ${l.type} invite link? Anyone holding it can no longer join.`)) return;
                            try {
                              await api.delete(`/teams/${teamId}/invite/invite-link/${l.token}`);
                              toast.success('Link deleted');
                              loadInviteLink();
                            } catch { toast.error('Failed to delete link'); }
                          }}
                          className="btn-ghost px-2 py-1 flex-shrink-0" style={{ fontSize: 11, color: 'var(--penalty, #e34a4a)' }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Unsent invites — full-width amber banner, easy to tap */}
            {unsentPending.length > 0 && (
              <button
                onClick={sendPendingInvites}
                disabled={sendingInvites}
                title="Send a Game Day invite email to all players imported but not yet invited."
                className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap"
                style={{ color: 'var(--puck)', border: '1px solid rgba(255,149,0,0.35)', background: 'rgba(255,149,0,0.07)' }}
              >
                {sendingInvites
                  ? <><div className="spinner" style={{width:11,height:11}} /> Sending invites…</>
                  : <><Mail size={12} /> Send {unsentPending.length} pending invite{unsentPending.length !== 1 ? 's' : ''} — tap to send now</>}
              </button>
            )}
          </div>
        )}
      </div>

      {/* v2.5.186 — rating legend strip removed at coach request.
          Every player row already shows its own rating badge, so the
          Elite/Strong/Average/Developing/Beginner key at the top was
          redundant and ate vertical space above the roster. */}

      {/* Apr 2026 — coach-only summary banner. Shown when one or more
          ACTIVE players (not pending invites, not deleted accounts)
          lack a position assignment. The team balancer needs every
          player's position to place them correctly; without one, the
          balancer falls back to "flex" and may put a defender on
          forward (or worse). The banner counts the affected players
          and links to a quick-fix mode (just expanding the player
          edit panel via row click) rather than navigating away.

          Why a banner: the per-row "⚠ No position" badge is great
          when the coach is already looking at a row, but it doesn't
          surface the issue when they're elsewhere on the page. The
          banner makes the count visible above the fold and motivates
          a fix before the next balance run.

          Hidden when there are no missing positions, so it doesn't
          add visual noise to teams that are fully set up. Only shown
          to coaches/managers — players don't see this. */}
      {isCoach && (() => {
        const missingPos = members.filter(m =>
          !m.isPending && !m.isDeleted
            && !m.position
            && (!Array.isArray(m.positions) || m.positions.length === 0)
        );
        if (missingPos.length === 0) return null;
        return (
          <div className="rounded-xl mb-3" style={{
            padding: '10px 14px',
            background: 'rgba(255,68,68,0.08)',
            border: '1px solid rgba(255,68,68,0.30)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--penalty)', margin: 0 }}>
                {missingPos.length === 1
                  ? `1 player has no position assigned`
                  : `${missingPos.length} players have no position assigned`}
              </p>
              <p style={{ fontSize: 11.5, color: 'var(--text-2)', margin: '3px 0 0', lineHeight: 1.4 }}>
                The team balancer can't place {missingPos.length === 1 ? 'this player' : 'these players'} on
                defense or forward properly without a position. Tap a row below
                with the <span style={{ color: 'var(--penalty)', fontWeight: 700 }}>⚠ No position</span> badge
                to set Forward, Defense, Goalie, or Flex.
              </p>
            </div>
          </div>
        );
      })()}

      {/* Apr 2026 — coach-only summary banner for MISSING RATINGS.
          Parallel to the missing-position banner above; both can show
          at once (independent issues). When a player has no rating,
          the balancer silently defaults them to 3 (average) — which
          works algorithmically but distorts team strength because
          the coach hasn't actually told the system how good they are.
          A "developing" player rated as "average" gets placed on the
          stronger team; a "skilled" player rated as "average" gets
          placed on the weaker team. Either way, balance suffers.

          Uses orange (var(--puck)) instead of red to distinguish from
          the missing-position banner — missing position is more
          severe (the player literally can't be placed correctly),
          missing rating is a degraded but still-functional state. */}
      {isCoach && (() => {
        // 2026-07-10 (coach-reported count bug) — pending invites were
        // EXCLUDED here, but they ARE included in team balancing (the
        // balancer reads team_invites.rating for checked-in invitees),
        // so right after a bulk import the banner wildly undercounted.
        // Count everyone balanceable, and show a denominator so the
        // number can be sanity-checked against the roster size.
        const activeRoster  = members.filter(m => !m.isDeleted);
        const missingRating = activeRoster.filter(m => !m.rating);
        if (missingRating.length === 0) return null;
        return (
          <div className="rounded-xl mb-3" style={{
            padding: '10px 14px',
            background: 'rgba(255,149,0,0.08)',
            border: '1px solid rgba(255,149,0,0.30)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}>
            <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0, marginTop: 1 }}>⚠️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--puck)', margin: 0 }}>
                {missingRating.length === 1
                  ? `1 of ${activeRoster.length} players has no rating`
                  : `${missingRating.length} of ${activeRoster.length} players have no rating`}
              </p>
              {/* 2026-07-12 (coach request) — NAME the unrated players so
                  the coach doesn't have to hunt through 58 rows for the
                  ⚠ badge. Show up to 8 names; beyond that the list would
                  wrap forever, so summarize the overflow. */}
              <p style={{ fontSize: 12, color: 'var(--puck)', margin: '3px 0 0', fontWeight: 600 }}>
                {missingRating.slice(0, 8).map(m => m.name || m.email || 'Unnamed player').join(', ')}
                {missingRating.length > 8 ? ` +${missingRating.length - 8} more` : ''}
              </p>
              <p style={{ fontSize: 11.5, color: 'var(--text-2)', margin: '3px 0 0', lineHeight: 1.4 }}>
                The balancer will treat {missingRating.length === 1 ? 'this player' : 'these players'} as
                average (3) until you set a rating. Tap a row with
                the <span style={{ color: 'var(--puck)', fontWeight: 700 }}>⚠ No rating</span> badge
                to assign Elite, Strong, Average, Developing, or Beginner.
              </p>
            </div>
          </div>
        );
      })()}

      {/* ── Search ── */}
      <div className="relative mb-3">
        <Search size={14} className="absolute left-3 top-3.5 text-secondary" />
        <input type="search" className="input-field pl-9 py-2.5 text-sm w-full"
          placeholder="Search players…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* ── Filter tabs ── */}
      <div className="flex gap-1.5 mb-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {FILTER_TABS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className="flex-shrink-0 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all"
            style={{
              background: filter === f.key ? 'var(--ice-a12)' : 'var(--surface-2)',
              border: `1px solid ${filter === f.key ? 'var(--ice-a30)' : 'var(--border)'}`,
              color: filter === f.key ? 'var(--ice)' : 'var(--text-secondary)',
              whiteSpace: 'nowrap',
            }}>
            {f.label}
            {(() => {
              if (f.key === 'all') return null;
              // Coaches chip counts by role directly (see filter memo
              // above — classifyMember no longer outputs 'coaches').
              // Apr 30 2026 (App_Change_List #1) — manager rows still
              // count under the Coaches chip; the user-facing label
              // collapses both staff roles into one.
              const count = f.key === 'coaches'
                ? members.filter(m => ['coach','manager'].includes(m.role)).length
                : (grouped[f.key]?.length ?? 0);
              return count > 0 ? <span style={{ marginLeft: 5, opacity: 0.65 }}>({count})</span> : null;
            })()}
          </button>
        ))}
      </div>

      {/* ── Skill / Sort / Season filters (Apr 30 2026, App_Change_List #21) ──
          Three additional axes that intersect with the search box and
          category chips above. Each is its own visually-distinct chip
          group so coaches can recognize them at a glance:
          • SKILL = rating chips with the established skill colours
          • SORT  = first-name vs last-name toggle
          • SEASON= dropdown (chips would overflow on teams with many seasons)

          Counts are not shown on these filters because they'd combinatorially
          interact with all the other axes (showing "5 players matching all
          filters" in 12 places adds noise without helping the choice). The
          empty state at the bottom of the list handles "nothing matches". */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 pb-3" style={{ borderBottom: '1px solid var(--border)' }}>
        {/* Skill chips */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase font-bold text-secondary tracking-wider" style={{ letterSpacing: '0.08em' }}>Skill</span>
          {[
            { key: 'all', label: 'Any', color: null },
            ...RATINGS.map(r => ({ key: String(r.value), label: String(r.value), color: r.color })),
          ].map(opt => {
            const active = skillFilter === opt.key;
            return (
              <button key={opt.key}
                onClick={() => setSkillFilter(opt.key)}
                className="py-1 px-2 rounded-md text-xs font-semibold transition-all"
                style={{
                  background: active && opt.color ? `${opt.color}24` : active ? 'var(--ice-a12)' : 'var(--surface-2)',
                  border: `1px solid ${active && opt.color ? opt.color : active ? 'var(--ice-a30)' : 'var(--border)'}`,
                  color: active && opt.color ? opt.color : active ? 'var(--ice)' : 'var(--text-secondary)',
                  minWidth: 28, lineHeight: 1.2,
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Sort toggle */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs uppercase font-bold text-secondary tracking-wider" style={{ letterSpacing: '0.08em' }}>Sort</span>
          {[
            { key: 'first', label: 'First name' },
            { key: 'last',  label: 'Last name'  },
          ].map(opt => {
            const active = sortBy === opt.key;
            return (
              <button key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className="py-1 px-2 rounded-md text-xs font-semibold transition-all"
                style={{
                  background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                  border: `1px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                  color: active ? 'var(--ice)' : 'var(--text-secondary)',
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* 2026-07-10 (batch-B A7, coach request) — Season dropdown
            REMOVED. The working season comes from the page context
            (seasonFilter stays synced to currentSeasonId via the
            existing effect); switching seasons happens on the Seasons
            page. Reset no longer touches the season axis for the same
            reason. */}

        {/* Reset — only appears when at least one non-default axis is set */}
        {(skillFilter !== 'all' || sortBy !== 'first') && (
          <button
            onClick={() => { setSkillFilter('all'); setSortBy('first'); }}
            className="text-xs font-semibold py-1 px-2 rounded-md ml-auto"
            style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
            }}>
            Reset filters
          </button>
        )}
      </div>

      {/* ── Empty state ── */}
      {filtered.length === 0 && (
        <div className="glass p-12 text-center text-secondary">
          <User size={40} className="mx-auto mb-3 opacity-20" />
          <p>{search ? `No players matching "${search}"` : 'No players in this category'}</p>
        </div>
      )}

      {/* ── Grouped sections ── */}
      {filtered.length > 0 && (
        <div className="space-y-5">
          {CATEGORY_DEFS.map(cat => {
            const catMembers   = grouped[cat.key] || [];
            if (!catMembers.length) return null;
            const isCollapsed  = collapsed.has(cat.key);
            const catSelectIds = catMembers.map(m => m.id);
            const allCatSel    = catSelectIds.length > 0 && catSelectIds.every(id => selected.has(id));
            const someCatSel   = catSelectIds.some(id => selected.has(id));

            return (
              <div key={cat.key}>
                {/* Section header */}
                <div className="flex items-center gap-2 mb-2 px-1">

                  {/* Collapse / expand — entire left side is the toggle */}
                  <button
                    onClick={() => setCollapsed(prev => { const n = new Set(prev); n.has(cat.key) ? n.delete(cat.key) : n.add(cat.key); return n; })}
                    style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer', flex: 1, padding: 0, textAlign: 'left' }}>
                    <span style={{ fontSize: 15 }}>{cat.emoji}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: cat.color }}>{cat.label}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: `${cat.color}15`, color: cat.color, border: `1px solid ${cat.color}30` }}>
                      {catMembers.length}
                    </span>
                    <span style={{ color: 'var(--text-2)', marginLeft: 4, display: 'flex', alignItems: 'center' }}>
                      {isCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    </span>
                  </button>

                  {/* Select all in category — explicit labelled button, coach only */}
                  {isCoach && catSelectIds.length > 0 && !isCollapsed && (
                    <button
                      onClick={() => toggleSelectCategory(cat.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '4px 11px', borderRadius: 7, flexShrink: 0, cursor: 'pointer',
                        fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
                        background: allCatSel
                          ? 'var(--ice-a20)'
                          : someCatSel
                          ? 'var(--ice-a12)'
                          : `${cat.color}18`,
                        border: `1px solid ${allCatSel || someCatSel ? 'var(--ice-a40)' : cat.color + '50'}`,
                        color: allCatSel || someCatSel ? 'var(--ice)' : cat.color,
                      }}>
                      <Edit2 size={10} />
                      {allCatSel ? 'Deselect group' : `Select group (${catSelectIds.length})`}
                    </button>
                  )}
                </div>

                {/* Members */}
                {!isCollapsed && (
                  <div className="space-y-1.5">
                    {catMembers.map(member => {
                      const ri         = getRating(member.rating);
                      const isEditing  = editId === member.id;
                      const isSelected = selected.has(member.id);
                      // Coaches/managers can be bulk-selected too now that they
                      // appear in their player classification. Server-side
                      // last-coach-delete guard still protects the team.
                      const canSelect  = isCoach;

                      return (
                        <div key={member.id} className="glass-hover rounded-xl overflow-hidden"
                          style={{ outline: isSelected ? '2px solid var(--ice)' : 'none', outlineOffset: 1 }}>

                          <div style={{ padding: isPhone ? '10px 12px' : '10px 10px' }}>
                            <div className="flex items-center gap-3">
                              {/* Checkbox */}
                              {canSelect && (
                                <button onClick={() => toggleSelect(member.id)}
                                  style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer', color: isSelected ? 'var(--ice)' : 'var(--border)', flexShrink: 0 }}>
                                  <CheckSquare size={16} />
                                </button>
                              )}

                              {/* Avatar — bigger on phones where the row is
                                  otherwise cramped with text + buttons. ── */}
                              <Avatar
                                avatarUrl={member.avatarUrl || null}
                                name={member.name || member.email}
                                size={isPhone ? 40 : 36}
                              />

                              {/* Name + meta (tap to open/close editor on mobile
                                  where the Edit button can be hard to reach
                                  once role badges and season badges wrap). */}
                              <div
                                className="flex-1 min-w-0"
                                role={isCoach ? 'button' : undefined}
                                tabIndex={isCoach ? 0 : undefined}
                                onClick={isCoach ? () => {
                                  setEditId(isEditing ? null : member.id);
                                  setEditForm({ rating: member.rating, position: member.position, positions: member.positions || [], memberRole: member.role, isSpare: !member.isPrimary, jerseyNumber: member.jerseyNumber, ratingNotes: member.ratingNotes || '', alias: member.alias || '' });
                                } : undefined}
                                onKeyDown={isCoach ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setEditId(isEditing ? null : member.id);
                                    setEditForm({ rating: member.rating, position: member.position, positions: member.positions || [], memberRole: member.role, isSpare: !member.isPrimary, jerseyNumber: member.jerseyNumber, ratingNotes: member.ratingNotes || '', alias: member.alias || '' });
                                  }
                                } : undefined}
                                style={isCoach ? { cursor: 'pointer' } : undefined}
                              >
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-semibold text-sm leading-tight" style={{ wordBreak: 'break-word' }}>
                                    {member.name || member.email?.split('@')[0] || '—'}
                                  </span>
                                  {/* Coach-only alias indicator. The backend
                                      sends `alias` only to coach/manager
                                      callers, so this is implicitly coach-
                                      scoped. Shows the alias in secondary
                                      colour next to the real name so the
                                      coach knows at a glance which players
                                      display under a different name to the
                                      rest of the team. ── */}
                                  {member.alias && (
                                    <span
                                      className="flex-shrink-0 text-xs"
                                      style={{ color: 'var(--text-3)', fontStyle: 'italic' }}
                                      title={`Other players see this person as "${member.alias}"`}>
                                      (aka {member.alias})
                                    </span>
                                  )}
                                  {['coach','manager'].includes(member.role) && (
                                    <span className="flex-shrink-0 inline-flex items-center gap-1"
                                      style={{
                                        background: 'var(--ice-a12)', color: 'var(--ice)',
                                        padding: isPhone ? '0 5px' : '2px 6px',
                                        borderRadius: 4,
                                        fontSize: isPhone ? 9 : 10,
                                        fontWeight: 700,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                        lineHeight: isPhone ? '16px' : '18px',
                                      }}>
                                      {/* Apr 30 2026 (App_Change_List #1) —
                                          two-role model: legacy managers
                                          display as "coach" in the UI so
                                          there's only one staff label
                                          users ever see. Backend still
                                          knows the original role. */}
                                      <Shield size={isPhone ? 8 : 9} /> coach
                                    </span>
                                  )}
                                  {/* Apr 30 2026 (App_Change_List #22) — position
                                      chip on the player card. Surfaces the
                                      player's role for balancing at a glance
                                      so coaches don't have to open the editor
                                      to see "is Bob a goalie or a flex skater?"
                                      Reads from `positions` (multi-select array
                                      preferred when populated; the coach-set
                                      master) with fallback to the single
                                      `position` field for legacy rows.
                                      Player-selected positions land here too —
                                      either via the invite acceptance flow
                                      (team_invites.positions → team_members.
                                      positions) or the onboarding
                                      preferred_positions → team_members
                                      propagation in auth.js.
                                      Suppressed when no position is set so
                                      the "⚠ No position" warning below is
                                      the only signal in that case (avoids
                                      a confusing empty chip).
                                      Coach override: tap-to-edit on the row
                                      already opens the position chip selector
                                      (lines ~2475-2505 below). The chip here
                                      is read-only — the editor is the override
                                      mechanism. */}
                                  {(() => {
                                    const arr = Array.isArray(member.positions) && member.positions.length > 0
                                      ? member.positions
                                      : (member.position ? [member.position] : []);
                                    if (arr.length === 0) return null;
                                    const POS_META = {
                                      forward: { emoji: '🏒', label: 'F',  full: 'Forward' },
                                      defense: { emoji: '🛡️', label: 'D',  full: 'Defence' },
                                      goalie:  { emoji: '🥅', label: 'G',  full: 'Goalie'  },
                                      flex:    { emoji: '🔁', label: 'Flex', full: 'Flex (any position)' },
                                    };
                                    const known = arr.map(k => POS_META[k]).filter(Boolean);
                                    if (known.length === 0) return null;
                                    const compact = known.length > 1
                                      ? known.map(p => p.label).join('/')
                                      : known[0].full;
                                    const isGoalie = arr.includes('goalie');
                                    const tip = arr.length > 1
                                      ? `Plays: ${known.map(p => p.full).join(', ')}. Tap row to override.`
                                      : `Plays ${known[0].full}. Tap row to override.`;
                                    return (
                                      <span className="flex-shrink-0"
                                        title={tip}
                                        style={{
                                          background: isGoalie ? 'rgba(0,232,122,0.10)' : 'var(--ice-a8, rgba(0,212,255,0.10))',
                                          color:      isGoalie ? 'var(--goal)' : 'var(--ice)',
                                          border: `1px solid ${isGoalie ? 'rgba(0,232,122,0.30)' : 'rgba(0,212,255,0.25)'}`,
                                          padding: isPhone ? '0 5px' : '2px 6px',
                                          borderRadius: 4,
                                          fontSize: isPhone ? 9 : 10,
                                          fontWeight: 700,
                                          letterSpacing: '0.03em',
                                          lineHeight: isPhone ? '16px' : '18px',
                                          whiteSpace: 'nowrap',
                                        }}>
                                        {known[0].emoji} {compact}
                                      </span>
                                    );
                                  })()}
                                  {member.isPending && member.missingEmail && (
                                    <span className="badge text-xs flex-shrink-0" style={{ background: 'rgba(255,59,59,0.12)', color: 'var(--penalty)', border: '1px solid rgba(255,59,59,0.25)' }}>
                                      ⚠ No email
                                    </span>
                                  )}
                                  {member.isPending && !member.missingEmail && member.inviteSent === true && (
                                    <span className="badge text-xs flex-shrink-0" style={{ background: 'rgba(0,232,122,0.15)', color: 'var(--goal)', border: '1px solid rgba(0,232,122,0.35)' }}>
                                      ✉ Invited
                                    </span>
                                  )}
                                  {member.isPending && !member.missingEmail && member.inviteSent === false && (
                                    <span className="badge text-xs flex-shrink-0" style={{ background: 'rgba(255,149,0,0.12)', color: 'var(--puck)', border: '1px solid rgba(255,149,0,0.3)', fontWeight: 700 }}>
                                      📋 Not Invited Yet
                                    </span>
                                  )}
                                  {/* Tombstone badge — shown when this player
                                      deleted their Game Day account. The team
                                      row survives (migration 036 changed the
                                      FK to ON DELETE SET NULL) so coaches
                                      keep their attendance history and can
                                      see at a glance that the spot is open
                                      for someone new. ── */}
                                  {member.isDeleted && (
                                    <span className="badge text-xs flex-shrink-0" style={{
                                      background: 'rgba(255,68,68,0.10)',
                                      color: 'var(--penalty)',
                                      border: '1px solid rgba(255,68,68,0.30)',
                                      fontWeight: 700,
                                    }}
                                    title={member.deletedAt ? `Account deleted on ${new Date(member.deletedAt).toLocaleDateString()}` : 'Account deleted'}>
                                      🗑 Account deleted
                                    </span>
                                  )}
                                  {/* Real member added manually but never logged in.
                                      Suppressed for tombstoned (deleted-account) rows —
                                      those legitimately have no lastLogin (the user
                                      row is gone) but the "🗑 Account deleted" badge
                                      already covers their state, and showing both
                                      is confusing ("did they never join, or did
                                      they join then leave?"). ── */}
                                  {/* Phase A (league gap-closure, 2026-06-09) — unclaimed
                                      league player: allocated from the league pool but no
                                      account yet. Links automatically once they register
                                      and verify the email on file. Takes precedence over
                                      the generic "Not joined yet" badge below. ── */}
                                  {member.isUnclaimed && (
                                    <span className="badge text-xs flex-shrink-0" style={{
                                      background: 'rgba(255,170,0,0.10)',
                                      color: 'var(--puck)',
                                      border: '1px solid rgba(255,170,0,0.30)',
                                      fontWeight: 700,
                                    }}
                                    title="Allocated by the league. Their roster spot links to their account automatically once they sign up with the email on file.">
                                      ⏳ Hasn't signed up
                                    </span>
                                  )}
                                  {!member.isPending && !member.isDeleted && !member.isUnclaimed && !member.lastLogin && member.email && (
                                    <span className="badge text-xs flex-shrink-0" style={{ background: 'rgba(0,212,255,0.08)', color: 'var(--ice)', border: '1px solid rgba(0,212,255,0.25)' }}>
                                      👤 Not joined yet
                                    </span>
                                  )}
                                  {/* 2026-09-17 (per Gilles) — the player picked a position in
                                      their own profile that differs from what the coach set.
                                      Coach's assignment still wins; this just flags it. */}
                                  {isCoach && Array.isArray(member.preferredPositions) && member.preferredPositions.length > 0 && (() => {
                                    const coachPos = (member.positions && member.positions.length)
                                      ? member.positions
                                      : (member.position && member.position !== 'flex' ? [member.position] : []);
                                    const prefs = member.preferredPositions.filter(p => p !== 'flex');
                                    if (prefs.length === 0) return null;
                                    const same = coachPos.length === prefs.length && prefs.every(p => coachPos.includes(p));
                                    if (same) return null;
                                    const label = prefs.map(p => POS_SHORT[p] || p).join('/');
                                    return (
                                      <span className="badge text-xs flex-shrink-0"
                                        style={{ background: 'rgba(167,139,250,0.10)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.35)' }}
                                        title={`This player set their preferred position to ${prefs.join(' / ')} in their profile — different from what you assigned (${coachPos.length ? coachPos.join(' / ') : 'flex'}).`}>
                                        💭 Prefers {label}
                                      </span>
                                    );
                                  })()}
                                  {/* Apr 2026 — missing-position warning. Shown when a
                                      player has NO position assigned (neither single
                                      `position` nor any entry in the `positions` array).
                                      The team balancer needs a position for every
                                      roster spot; a player without one ends up
                                      unassignable, and the coach won't notice until
                                      they hit Generate Balanced Teams. Surfacing the
                                      warning right on the row prompts a fix before
                                      that point. Suppressed for tombstones, deleted
                                      accounts, and pending invites (those are
                                      pre-roster).

                                      Apr 2026 (v2): no longer gated on season
                                      membership. Many teams don't use seasons at all,
                                      and the badge is just as useful for them — a
                                      checked-in player without a position can't be
                                      placed by the balancer regardless of whether
                                      they're in a season. */}
                                  {!member.isPending && !member.isDeleted
                                    && !member.position
                                    && (!Array.isArray(member.positions) || member.positions.length === 0) && (
                                    <span className="badge text-xs flex-shrink-0" style={{
                                      background: 'rgba(255,68,68,0.12)',
                                      color: 'var(--penalty)',
                                      border: '1px solid rgba(255,68,68,0.30)',
                                      fontWeight: 700,
                                    }}
                                    title="This player has no position assigned. Set one (Forward, Defense, Goalie, or Flex) so the team balancer can place them correctly.">
                                      ⚠ No position
                                    </span>
                                  )}
                                  {/* Apr 2026 — missing-rating warning. Parallel to the
                                      "No position" badge above. The balancer falls back
                                      to a silent default (rating 3 = average) for any
                                      player without a rating, which means they get
                                      assigned to teams as if they were average — this
                                      can drag a strong team down or pull a weak team up
                                      without the coach realizing. Surfacing it here
                                      prompts the coach to set a real rating before the
                                      next balance.

                                      Same suppression rules as the position badge:
                                      pending invites, deleted accounts, and tombstones
                                      don't show it (they're pre-roster). */}
                                  {/* Apr 2026 — gate the "No rating" badge
                                      on isCoach. Non-coaches reaching this
                                      page (e.g. via a stale URL bookmark
                                      since we've hidden the nav link) get
                                      no hint that ratings exist when the
                                      team has chosen not to show them. */}
                                  {isCoach && !member.isPending && !member.isDeleted && !member.rating && (
                                    <span className="badge text-xs flex-shrink-0" style={{
                                      background: 'rgba(255,149,0,0.12)',
                                      color: 'var(--puck)',
                                      border: '1px solid rgba(255,149,0,0.30)',
                                      fontWeight: 700,
                                    }}
                                    title="This player has no rating. The balancer will treat them as average (3) until you set one — tap the row to assign Elite/Strong/Average/Developing/Beginner.">
                                      ⚠ No rating
                                    </span>
                                  )}
                                  {/* Account status from email lookup */}
                                  {emailStatus[member.inviteId]?.status === 'verified' && (
                                    <span className="badge text-xs flex-shrink-0" style={{ background: 'rgba(0,232,122,0.12)', color: 'var(--goal)', border: '1px solid rgba(0,232,122,0.3)' }}>
                                      ✓ Game Day account
                                    </span>
                                  )}
                                  {emailStatus[member.inviteId]?.status === 'unverified' && (
                                    <span className="badge text-xs flex-shrink-0" style={{ background: 'rgba(255,149,0,0.10)', color: 'var(--puck)', border: '1px solid rgba(255,149,0,0.25)' }}>
                                      ⏳ Unverified account
                                    </span>
                                  )}
                                  {/* May 4 2026 — per-row season chips removed.
                                      The Players page is now scoped to the current
                                      (active) season; players in this list ARE in
                                      this season by definition, so showing chips
                                      saying "Summer 2026 Sunday Hockey" on every
                                      row was redundant noise. The full season
                                      membership list is still editable on the
                                      per-player edit panel for coaches who want
                                      to manage cross-season presence. ── */}
                                </div>
                                {/* Secondary meta row — suppressed entirely when
                                    empty on phones so we don't reserve vertical
                                    space for nothing. ── */}
                                {(() => {
                                  const hasJersey = !!member.jerseyNumber;
                                  const hasEmail  = isCoach && member.email && !isPhone;
                                  const hasMissingEmailBtn = isCoach && member.isPending && member.missingEmail;
                                  if (!hasJersey && !hasEmail && !hasMissingEmailBtn) return null;
                                  return (
                                    <div className="flex items-center gap-2 mt-0.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                                      {hasJersey && <span className="flex-shrink-0">#{member.jerseyNumber}</span>}
                                      {hasEmail && (
                                        <span className="flex items-center gap-1 truncate" style={{ minWidth: 0 }}>
                                          <span className="truncate" style={{ minWidth: 0 }}>{member.email}</span>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); copySingleEmail(member.email); }}
                                            title="Copy email"
                                            className="flex-shrink-0"
                                            style={{
                                              background: 'none', border: 'none', cursor: 'pointer',
                                              color: 'var(--text-secondary)', padding: 2,
                                              display: 'inline-flex', alignItems: 'center',
                                              opacity: 0.6,
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                            onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}>
                                            <Copy size={10} />
                                          </button>
                                        </span>
                                      )}
                                      {hasMissingEmailBtn && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setEmailEditing(member.inviteId); setEmailDraft(''); }}
                                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--penalty)', fontSize: 11, fontWeight: 700, padding: 0, textDecoration: 'underline' }}>
                                          + Add email address
                                        </button>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Coach actions */}
                              {isCoach && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {/* One-tap Send: pending invites not yet sent. Stops
                                      propagation so it doesn't also open the editor. */}
                                  {member.isPending && !member.missingEmail && member.inviteSent === false && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); sendSinglePending(member); }}
                                      disabled={sendingSingle === member.id}
                                      title="Send invite email"
                                      style={{
                                        width: 32, height: 32, borderRadius: 8, padding: 0,
                                        background: 'rgba(0,232,122,0.1)', border: '1px solid rgba(0,232,122,0.28)',
                                        color: 'var(--goal)', cursor: 'pointer', touchAction: 'manipulation',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                      {sendingSingle === member.id
                                        ? <div className="spinner" style={{ width: 11, height: 11 }} />
                                        : <Mail size={14} />}
                                    </button>
                                  )}
                                  {/* AI rating — hidden on phones to save horizontal space.
                                      The same button is available inside the expanded edit
                                      panel (opens when you tap the name or Edit icon) so no
                                      functionality is lost on mobile. ── */}
                                  {/* Apr 30 2026 (App_Change_List #24) — AI rating
                                      suggestion (Star) button removed. Coach feedback:
                                      not currently useful, takes up valuable row real
                                      estate next to Edit/Delete. requestAiRating /
                                      aiSuggestions / aiLoading state are kept in the
                                      file (still referenced by some callers) but the
                                      UI entry points are gone. Re-add by uncommenting
                                      this block AND the panel below if/when the
                                      feature is wanted again. */}
                                  {/* Edit — on phones we collapse to an icon-only pencil
                                      to save space. On tablets/desktop the "Edit" label
                                      is kept for clarity. The pill's background still
                                      reflects the open/closed state. ── */}
                                  <button onClick={(e) => {
                                    e.stopPropagation();
                                    // If a delete was armed and user taps Edit, cancel the armed delete
                                    // silently — Edit is a non-destructive action, no reason to leave
                                    // the Delete button waiting.
                                    if (removeConfirmId === member.id) setRemoveConfirmId(null);
                                    setEditId(isEditing ? null : member.id);
                                    setEditForm({ rating: member.rating, position: member.position, positions: member.positions || [], memberRole: member.role, isSpare: !member.isPrimary, jerseyNumber: member.jerseyNumber, ratingNotes: member.ratingNotes || '', alias: member.alias || '' });
                                  }} style={{
                                    height: 32,
                                    width: isPhone ? 32 : 'auto',
                                    padding: isPhone ? 0 : '0 11px',
                                    borderRadius: 8,
                                    fontSize: 11, fontWeight: 700,
                                    background: isEditing ? 'var(--ice-a12)' : 'var(--surface-2)',
                                    border: `1px solid ${isEditing ? 'var(--ice-a30)' : 'var(--border)'}`,
                                    color: isEditing ? 'var(--ice)' : 'var(--text-2)',
                                    cursor: 'pointer', touchAction: 'manipulation', transition: 'all 0.15s',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                  }}
                                  title={isEditing ? 'Close editor' : 'Edit player'}>
                                    {isPhone
                                      ? (isEditing ? <X size={15} /> : <Edit2 size={14} />)
                                      : (isEditing ? 'Close' : 'Edit')}
                                  </button>
                                  {/* Delete button — armed first tap, commits second tap. Extra
                                      margin-left so an accidental tap on Edit doesn't land on
                                      Delete. When armed, a cancel (×) appears to its left so
                                      the user can explicitly back out. */}
                                  {removeConfirmId === member.id && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setRemoveConfirmId(null); }}
                                      title="Cancel"
                                      style={{
                                        width: 32, height: 32, padding: 0, borderRadius: 8,
                                        background: 'var(--surface-2)',
                                        border: '1px solid var(--border)',
                                        color: 'var(--text-2)',
                                        cursor: 'pointer', touchAction: 'manipulation',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: 14, fontWeight: 700, lineHeight: 1,
                                      }}>
                                      ✕
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => { e.stopPropagation(); removeMember(member); }}
                                    title={removeConfirmId === member.id ? 'Tap again to confirm delete' : 'Remove from team'}
                                    style={{
                                      width: removeConfirmId === member.id && !isPhone ? 'auto' : 32,
                                      height: 32,
                                      padding: removeConfirmId === member.id && !isPhone ? '0 10px' : 0,
                                      borderRadius: 8,
                                      // Explicit left margin so Delete isn't crammed up against Edit —
                                      // prevents accidental delete taps.
                                      marginLeft: isPhone ? 4 : 8,
                                      background: removeConfirmId === member.id ? 'var(--penalty)' : 'rgba(255,59,59,0.08)',
                                      border: `1px solid ${removeConfirmId === member.id ? 'var(--penalty)' : 'rgba(255,59,59,0.25)'}`,
                                      color: removeConfirmId === member.id ? '#fff' : 'var(--penalty)',
                                      cursor: 'pointer', touchAction: 'manipulation',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                      fontSize: 11, fontWeight: 700,
                                    }}>
                                    <Trash2 size={14} />
                                    {removeConfirmId === member.id && !isPhone && <span>Confirm?</span>}
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Rating pills — skaters/goalies only, not coaches.
                                Hidden on phones (they'd push the card to a 3rd
                                line at the tight margin); the full rating
                                selector lives inside the edit panel which is
                                a tap on the name away. On tablets+ we keep
                                the quick-access inline row. ── */}
                            {isCoach && !['coach','manager'].includes(member.role) && !isPhone && (
                              <div className="flex items-center gap-1 mt-2" style={{ marginLeft: canSelect ? 54 : 46 }}>
                                {savingRating === member.id
                                  ? <div className="spinner" style={{width:13,height:13}} />
                                  : RATINGS.map(r => (
                                    <button key={r.value} onClick={(e) => { e.stopPropagation(); quickRating(member, r.value); }} title={r.label}
                                      style={{ minWidth: 30, height: 26, borderRadius: 7, flexShrink: 0, padding: '0 6px', background: member.rating === r.value ? `${r.color}22` : 'var(--surface-2)', border: `1.5px solid ${member.rating === r.value ? r.color : 'var(--border)'}`, color: member.rating === r.value ? r.color : 'var(--text-2)', fontSize: 11, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                                      {member.rating === r.value && <span style={{ fontSize: 9 }}>{r.emoji}</span>}
                                      {r.value}
                                    </button>
                                  ))
                                }
                                {member.rating && (() => { const rated = getRating(member.rating); return rated ? <span className="text-xs ml-1" style={{ color: rated.color, fontWeight: 600 }}>{rated.label}</span> : null; })()}
                              </div>
                            )}

                            {/* Phone: tiny rating badge in closed row — shows
                                the CURRENT rating compactly (e.g. "⭐ 3 •
                                Average") so the coach can see each player's
                                rating at a glance without needing to tap
                                into the edit panel. Tapping it opens the
                                edit panel (via the parent name row's click
                                handler since this element doesn't stop
                                propagation).
                                Apr 2026 (Tom_Notes J5/J6) — `isCoach` gate
                                removed. The backend now filters
                                `member.rating` to NULL for non-coach viewers
                                when `show_ratings_to_players=false` (and
                                always exposes the viewer's own rating per
                                the own-rating exception). So we render
                                whenever a rating value is present — the
                                viewer either is the coach, the team has
                                ratings public, or it's their own rating. ── */}
                            {isPhone && !['coach','manager'].includes(member.role) && member.rating && (() => {
                              const rated = getRating(member.rating);
                              if (!rated) return null;
                              return (
                                <div className="mt-1.5" style={{ marginLeft: canSelect ? 54 : 46 }}>
                                  <span
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      background: `${rated.color}15`,
                                      border: `1px solid ${rated.color}40`,
                                      color: rated.color,
                                      fontSize: 10.5, fontWeight: 700,
                                      padding: '2px 8px', borderRadius: 6,
                                      letterSpacing: '0.02em',
                                    }}>
                                    <span>{rated.emoji}</span>
                                    <span>{member.rating}</span>
                                    <span style={{ opacity: 0.8, fontWeight: 600 }}>· {rated.label}</span>
                                  </span>
                                </div>
                              );
                            })()}

                            {!isCoach && member.rating && (
                              <div className="mt-1.5 ml-12">
                                <span className="badge text-xs" style={{ background: 'var(--surface-3)', color: 'var(--text-secondary)' }}>{member.position || 'player'}</span>
                              </div>
                            )}
                          </div>

                          {/* Email editor for name-only imports */}
                          {isCoach && member.isPending && member.missingEmail && emailEditing === member.inviteId && (
                            <div className="px-3 pb-3 pt-3 border-t border-white/6" style={{ background: 'rgba(255,59,59,0.04)' }}>
                              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--penalty)' }}>
                                Add email address for {member.name || 'this player'}
                              </p>
                              <div className="flex gap-2">
                                <input
                                  type="email"
                                  autoFocus
                                  placeholder="player@example.com"
                                  value={emailDraft}
                                  onChange={e => setEmailDraft(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') saveInviteEmail(member); if (e.key === 'Escape') setEmailEditing(null); }}
                                  className="input-field flex-1 py-1.5 text-sm"
                                />
                                <button onClick={() => saveInviteEmail(member)} disabled={emailSaving || !emailDraft.trim()}
                                  className="btn-ice text-xs px-3 flex-shrink-0">
                                  {emailSaving ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Save'}
                                </button>
                                <button onClick={() => setEmailEditing(null)} className="btn-ghost text-xs px-2 flex-shrink-0">✕</button>
                              </div>
                              <p className="text-xs mt-1.5" style={{ color: 'var(--text-2)' }}>
                                We'll check if they already have a Game Day account.
                              </p>
                            </div>
                          )}

                          {/* Apr 30 2026 (App_Change_List #24) — AI suggestion
                              result panel removed alongside the Star trigger
                              button. aiSuggestions state retained for back-
                              compat (no callers actively populate it now). */}

                          {/* Inline edit panel */}
                          {isEditing && isCoach && (
                            <div className="px-4 pb-4 pt-0 border-t border-white/6 mt-0" style={{ background: 'var(--ice-a3)' }}>
                              <div className="grid gap-3 mt-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
                                <div>
                                  <label className="label">Rating</label>
                                  <div className="flex gap-1.5">
                                    {RATINGS.map(r => (
                                      <button key={r.value} onClick={() => setEditForm(p => ({ ...p, rating: r.value }))} className="flex-1 py-2 rounded-lg text-sm font-bold transition-all"
                                        style={{ background: editForm.rating === r.value ? `${r.color}20` : 'var(--surface-2)', border: `1px solid ${editForm.rating === r.value ? r.color : 'transparent'}`, color: editForm.rating === r.value ? r.color : 'var(--text-secondary)' }}>
                                        {r.value}
                                      </button>
                                    ))}
                                  </div>
                                  {/* Apr 30 2026 (App_Change_List #24) — second
                                      AI Rating Suggestion entry point inside the
                                      edit panel removed too. The original was
                                      kept for phone feature-parity with the row
                                      Star button; with the row button gone there's
                                      no parity to maintain. */}
                                </div>
                                <div>
                                  <label className="label">Classification</label>
                                  <select className="input-field py-2"
                                    value={editForm.position === 'goalie' ? (editForm.isSpare ? 'goalie_spare' : 'goalie_full') : (editForm.isSpare ? 'player_spare' : 'player_full')}
                                    onChange={e => {
                                      const cls = CLASSIFY_PUT[e.target.value];
                                      if (!cls) return;
                                      // May 1 2026 — Classification change now ALSO syncs the
                                      // positions[] array so the row badge reflects the new
                                      // classification immediately. Previously: choosing "Goalie
                                      // FT" set position='goalie' but left positions=[] (or
                                      // whatever skater chips the coach had picked before),
                                      // so the row badge could keep showing "F/D" for someone
                                      // the coach just classified as a goalie.
                                      //
                                      // Logic:
                                      //   • Goalie classification → positions = ['goalie'],
                                      //     position singular = 'goalie'
                                      //   • Player classification → strip 'goalie' from any
                                      //     existing positions (they're not a goalie any
                                      //     more); singular derived from remaining chips:
                                      //       length=0 → 'flex'
                                      //       length=1 → that one
                                      //       length>=2 → first surviving chip (consistent
                                      //         with the chip-click rule below)
                                      setEditForm(p => {
                                        if (cls.position === 'goalie') {
                                          return { ...p, position: 'goalie', positions: ['goalie'], isSpare: cls.isSpare, memberRole: cls.memberRole };
                                        }
                                        // Skater classification — drop 'goalie' if present
                                        const survivingChips = (Array.isArray(p.positions) ? p.positions : []).filter(x => x !== 'goalie');
                                        let nextSingle;
                                        if (survivingChips.length === 0) {
                                          nextSingle = 'flex';
                                        } else if (survivingChips.length === 1) {
                                          nextSingle = survivingChips[0];
                                        } else {
                                          // Multiple skater chips survive — keep the prior
                                          // singular if it survives, else first chip.
                                          if (p.position && p.position !== 'goalie' && survivingChips.includes(p.position)) {
                                            nextSingle = p.position;
                                          } else {
                                            nextSingle = survivingChips[0];
                                          }
                                        }
                                        return { ...p, position: nextSingle, positions: survivingChips, isSpare: cls.isSpare, memberRole: cls.memberRole };
                                      });
                                    }}>
                                    {CATEGORY_DEFS.filter(c => c.key !== 'coaches').map(c => (
                                      <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>
                                    ))}
                                  </select>
                                </div>
                                {/* Role — available for all members including pending */}
                                <div>
                                  <label className="label">Role</label>
                                  <select className="input-field py-2" value={editForm.memberRole || 'player'} onChange={e => setEditForm(p => ({ ...p, memberRole: e.target.value }))}>
                                    <option value="player">Player</option>
                                    <option value="coach">Coach</option>
                                    {/* Apr 30 2026 (App_Change_List #1) — Manager
                                        option removed; legacy managers in DB
                                        keep their permissions but cannot be
                                        newly assigned. If editing a member who
                                        IS already a manager, the select will
                                        show no current value matching — the
                                        defaultValue handler below adds back
                                        an inert "Manager (legacy)" entry so
                                        we don't silently flip them to player. */}
                                    {editForm.memberRole === 'manager' && (
                                      <option value="manager" disabled>Manager (legacy — pick Coach to migrate)</option>
                                    )}
                                  </select>
                                </div>
                                {/* Jersey # and Coach Notes only apply to real members */}
                                {!member.isPending && (
                                  <div>
                                    <label className="label">Jersey #</label>
                                    <input type="number" min="1" max="99" className="input-field py-2"
                                      value={editForm.jerseyNumber || ''} onChange={e => setEditForm(p => ({ ...p, jerseyNumber: e.target.value }))} />
                                  </div>
                                )}
                              </div>
                              {/* Positions — multi-select (applies to ALL members
                                  including coaches and managers).
                                  Apr 2026: previously gated on
                                  `!['coach','manager'].includes(memberRole)` which
                                  hid the picker for coaches who also play. Coaches
                                  who play get the same "⚠ No position" warning as
                                  any other player on the team list and the balancer
                                  needs their position to place them correctly, so
                                  the picker is now shown universally. Coaches who
                                  ONLY coach (don't play) can simply leave it blank
                                  — the warning still shows but they can ignore it,
                                  or set 'Flex' as a safe catch-all. */}
                              <div className="mt-3">
                                <label className="label">Positions they can play</label>
                                <p className="text-xs text-secondary mb-2" style={{ marginTop: -2, lineHeight: 1.4 }}>
                                  Pick one or more. The balancer uses these to assign teams.
                                  Leave all unchecked = "Flex" (any position).
                                </p>
                                <div className="flex gap-2 flex-wrap mt-1">
                                  {[
                                    { key: 'forward', emoji: '🏒', label: 'Forward' },
                                    { key: 'defense', emoji: '🛡️', label: 'Defence' },
                                    { key: 'goalie',  emoji: '🥅', label: 'Goalie' },
                                  ].map(opt => {
                                    const active = (editForm.positions || []).includes(opt.key);
                                    return (
                                      <button
                                        key={opt.key}
                                        type="button"
                                        onClick={() => {
                                          // May 1 2026 (post-deploy bug 3 round 2) — strip
                                          // 'flex' from the array on every chip interaction.
                                          // Migration 020 backfilled positions = ARRAY[position]
                                          // for existing rows, so a player whose old singular
                                          // was 'flex' got positions=['flex'] persisted. When
                                          // the coach later picks a real chip (Forward), this
                                          // handler used to do [...cur, 'forward'] which ended
                                          // up as ['flex', 'forward'] in the DB and rendered
                                          // as "Flex/F" on the row badge. 'flex' means "no
                                          // specific position chosen" — it's mutually
                                          // exclusive with F/D/G. So strip it whenever a real
                                          // chip is being toggled.
                                          const curRaw = editForm.positions || [];
                                          const cur = curRaw.filter(k => k !== 'flex');
                                          const next = active ? cur.filter(k => k !== opt.key) : [...cur, opt.key];
                                          // Keep the legacy `position` singular in sync with
                                          // the chips so consumers that read the singular
                                          // column see something sensible. Rules (conservative
                                          // on multi-position so we don't unilaterally re-
                                          // bucket a hybrid player on the GamePage):
                                          //   • Empty chips → singular = 'flex'
                                          //   • Exactly one chip → singular = that one
                                          //   • Two or more chips → keep current singular
                                          //     IF it's still in the chips array; otherwise
                                          //     fall back to the first chip.
                                          setEditForm(p => {
                                            let nextSingle;
                                            if (next.length === 0) {
                                              nextSingle = 'flex';
                                            } else if (next.length === 1) {
                                              nextSingle = next[0];
                                            } else {
                                              const currSingle = p.position;
                                              if (currSingle && currSingle !== 'flex' && next.includes(currSingle)) {
                                                nextSingle = currSingle;
                                              } else {
                                                nextSingle = next[0];
                                              }
                                            }
                                            return { ...p, positions: next, position: nextSingle };
                                          });
                                        }}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all"
                                        style={{
                                          background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                                          border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                                          color: active ? 'var(--ice)' : 'var(--text-2)',
                                          cursor: 'pointer',
                                        }}>
                                        {opt.emoji} {opt.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              {!member.isPending && (
                                <div className="mt-3">
                                  <label className="label">Coach Notes <span className="text-xs opacity-50">(private)</span></label>
                                  <textarea rows={2} className="input-field py-2 resize-none text-sm"
                                    placeholder="Internal notes about this player's skills, injuries, etc."
                                    value={editForm.ratingNotes || ''} onChange={e => setEditForm(p => ({ ...p, ratingNotes: e.target.value }))} />
                                </div>
                              )}
                              {member.isPending && (
                                <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                                  This player hasn't joined yet — only Rating and Classification can be saved now.
                                </p>
                              )}

                              {/* ── Display Alias (Apr 2026) ──
                                  Optional display name that replaces the player's
                                  real name for OTHER players (not coaches). Useful
                                  for privacy, nicknames, or keeping the roster
                                  feeling casual. Coaches still see the real
                                  name everywhere; the alias only substitutes in
                                  game rosters, schedules, chat, and balanced-
                                  teams UIs when a non-coach is viewing. Blank
                                  input clears the alias. Real members only —
                                  pending invites don't have a team_member row
                                  yet so there's nothing to attach the alias to.
                                  Shown regardless of role (player vs coach) so
                                  a coach can clear an alias that was set before
                                  they were promoted; for coach/manager roles
                                  the helper text is adjusted to note the
                                  alias still shows to non-coach viewers if
                                  left set. ── */}
                              {!member.isPending && (
                                <div className="mt-3">
                                  <label className="label">
                                    Display Alias
                                    <span className="text-xs opacity-60 ml-1">(optional — shown to other players)</span>
                                  </label>
                                  <input
                                    type="text"
                                    maxLength={40}
                                    className="input-field py-2 text-sm"
                                    placeholder="e.g. Ghost, Lefty, etc. (blank to use real name)"
                                    value={editForm.alias || ''}
                                    onChange={e => setEditForm(p => ({ ...p, alias: e.target.value }))} />
                                  <p className="text-xs mt-1" style={{ color: 'var(--text-3)', lineHeight: 1.4 }}>
                                    {['coach','manager'].includes(editForm.memberRole)
                                      ? 'Coaches usually play under their real name — clear this field if you don\'t want other players to see the alias.'
                                      : 'Shown to other players in games, chat, schedule, and balanced teams. Coaches always see the real name.'}
                                  </p>
                                </div>
                              )}

                              {/* ── Email — coach/manager can fix a pending invite's email address.
                                  For accepted users (real members) we intentionally DO NOT let the
                                  coach change their email because that would silently change their
                                  login credentials. Players change their own email from Profile. ── */}
                              <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                                <label className="label">Email</label>
                                {member.isPending ? (
                                  emailEditing === member.inviteId ? (
                                    <div className="flex gap-2 mt-1">
                                      <input
                                        type="email"
                                        autoFocus
                                        placeholder="player@example.com"
                                        value={emailDraft}
                                        onChange={e => setEmailDraft(e.target.value)}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') saveInviteEmail(member);
                                          if (e.key === 'Escape') setEmailEditing(null);
                                        }}
                                        className="input-field flex-1 py-1.5 text-sm"
                                      />
                                      <button onClick={() => saveInviteEmail(member)} disabled={emailSaving || !emailDraft.trim()}
                                        className="btn-ice text-xs px-3 flex-shrink-0">
                                        {emailSaving ? <div className="spinner" style={{ width: 12, height: 12 }} /> : 'Save'}
                                      </button>
                                      <button onClick={() => setEmailEditing(null)} className="btn-ghost text-xs px-2 flex-shrink-0">✕</button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="flex-1 text-sm truncate" style={{
                                        color: member.email ? 'var(--text)' : 'var(--penalty)',
                                        fontFamily: member.email ? 'inherit' : 'inherit',
                                      }}>
                                        {member.email || '⚠ No email on file'}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEmailDraft(member.email || '');
                                          setEmailEditing(member.inviteId);
                                        }}
                                        className="btn-ghost text-xs px-3 py-1.5 flex-shrink-0">
                                        <Edit2 size={11} /> {member.email ? 'Edit' : 'Add Email'}
                                      </button>
                                    </div>
                                  )
                                ) : (
                                  // Accepted user — show read-only + explanation why edits go through Profile
                                  <>
                                    <div className="text-sm mt-1" style={{ color: 'var(--text)' }}>
                                      {member.email || <span style={{ color: 'var(--text-3)' }}>—</span>}
                                    </div>
                                    <p className="text-xs mt-1.5" style={{ color: 'var(--text-2)', lineHeight: 1.5 }}>
                                      This player has a Game Day account. Email changes must be done by the player themselves
                                      from their Profile page, since the email is also their login.
                                    </p>
                                  </>
                                )}
                              </div>

                              {/* ── Seasons — assign this player individually to one or more seasons.
                                  Unlike the other fields in this panel, season changes take effect
                                  immediately (they're separate API calls, not part of the member PUT).
                                  The "Save" button still only saves rating/role/notes.

                                  Apr 30 2026 (App_Change_List #25) — when displayed on a
                                  player's row, the chip list is now filtered to the team's
                                  CURRENT ACTIVE season(s) only. Coach feedback: showing
                                  every historical season chip on every player row was
                                  visually noisy and made it look like the player was
                                  "currently in" 6 seasons when most of those were
                                  archived. Add/remove still works; archived-season
                                  memberships are preserved silently in the backend
                                  (member.seasons still arrives complete; we just hide
                                  the archived ones from this view). The mutually-
                                  exclusive seasons rework in Batch L will further
                                  collapse this to a single chip, but for now we keep
                                  the multi-chip pattern in case a coach is mid-
                                  transition between seasons.
                                  v2.5.101 — coach feedback: "I'm still seeing a
                                  schedule when I search for a player. There's a
                                  schedule with the schedule name on it, but all the
                                  players are already underneath a schedule so we
                                  don't need to show that anymore." When the team
                                  has a single active season AND the player is
                                  already in it, the entire SCHEDULES block adds zero
                                  information — it's just clutter on the edit modal.
                                  Hide in that common case. We KEEP it for multi-
                                  season teams (some run a Sunday + Tuesday league
                                  in parallel) and when the player isn't yet in
                                  every active season — both real management
                                  surfaces. ── */}
                              {(() => {
                                const activeSeasonsList = seasons.filter(s => s.isActive !== false);
                                const memberActiveIds = (member.seasons || [])
                                  .filter(s => activeSeasonsList.some(a => a.id === s.id))
                                  .map(s => s.id);
                                const isRedundant = activeSeasonsList.length <= 1
                                  && activeSeasonsList.every(a => memberActiveIds.includes(a.id));
                                if (isRedundant) return null;
                                return (
                              <div className="mt-4 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                                {(() => {
                                  // Active = NOT archived (isActive !== false). Mirrors
                                  // the same predicate used by the add-dropdown below
                                  // so the two views stay in sync.
                                  const activeSeasonIds = new Set(
                                    seasons.filter(s => s.isActive !== false).map(s => s.id)
                                  );
                                  const visibleSeasons = (member.seasons || [])
                                    .filter(s => activeSeasonIds.has(s.id));
                                  return (<>
                                <label className="label flex items-center justify-between">
                                  <span>Schedules</span>
                                  {visibleSeasons.length > 0 && (
                                    <span className="text-xs opacity-60">
                                      {visibleSeasons.length} active
                                    </span>
                                  )}
                                </label>

                                {/* Current memberships — chips with × to remove.
                                    Archived memberships hidden (see comment above). */}
                                {visibleSeasons.length > 0 ? (
                                  <div className="flex flex-wrap gap-1.5 mb-2">
                                    {visibleSeasons.map(s => (
                                      <button
                                        key={s.seasonMemberId || s.id}
                                        onClick={() => removeMemberFromSeason(member, s)}
                                        title={`Remove from ${s.name}`}
                                        disabled={removingFromSeason === s.seasonMemberId}
                                        style={{
                                          display: 'inline-flex', alignItems: 'center', gap: 5,
                                          padding: '4px 8px 4px 10px',
                                          borderRadius: 999,
                                          background: 'rgba(167,139,250,0.12)',
                                          border: '1px solid rgba(167,139,250,0.3)',
                                          color: '#a78bfa',
                                          fontSize: 12, fontWeight: 600,
                                          cursor: removingFromSeason === s.seasonMemberId ? 'wait' : 'pointer',
                                          opacity: removingFromSeason === s.seasonMemberId ? 0.5 : 1,
                                        }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                                          {s.name}
                                        </span>
                                        <span style={{
                                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                          width: 16, height: 16, borderRadius: '50%',
                                          background: 'rgba(167,139,250,0.25)', fontSize: 11, lineHeight: 1,
                                        }}>×</span>
                                      </button>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-xs text-secondary mb-2">Not assigned to any active season yet.</p>
                                )}
                                  </>);
                                })()}

                                {/* Add-to-season dropdown — only lists seasons the player isn't already in.
                                    Apr 2026 — also excludes archived
                                    seasons so coaches don't add new
                                    members to wrapped-up rosters. */}
                                {(() => {
                                  const assignedIds = new Set((member.seasons || []).map(s => s.id));
                                  const available = seasons.filter(s =>
                                    !assignedIds.has(s.id) && s.isActive !== false
                                  );
                                  if (seasons.filter(s => s.isActive !== false).length === 0) {
                                    return (
                                      <p className="text-xs text-secondary italic">
                                        No active seasons exist yet. Create one from the Seasons page first.
                                      </p>
                                    );
                                  }
                                  if (available.length === 0) {
                                    return (
                                      <p className="text-xs text-secondary italic">
                                        Already in all active seasons.
                                      </p>
                                    );
                                  }
                                  return (
                                    <select
                                      className="input-field py-2 text-sm"
                                      value=""
                                      disabled={!!addingToSeason}
                                      onChange={(e) => {
                                        const season = seasons.find(s => s.id === e.target.value);
                                        if (season) addMemberToSeason(member, season);
                                        e.target.value = ''; // reset so same season can be re-picked after removal
                                      }}>
                                      <option value="" disabled>
                                        {addingToSeason ? 'Adding…' : '+ Add to a season…'}
                                      </option>
                                      {available.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                      ))}
                                    </select>
                                  );
                                })()}
                              </div>
                                );
                              })()}

                              {/* ── Invite actions — above Cancel/Save so they're always visible ── */}

                              {/* ── Unified invite button — visible on every
                                  player in the edit panel when an email is
                                  on file. Works regardless of whether the
                                  player is a real member with an account, a
                                  pending invite that's been sent, or a
                                  pending invite that's still unsent. Coach
                                  may need to re-send for any reason:
                                  typo, user deleted the email, bounced, etc.

                                  Routing:
                                    - Pending invite → POST /import-players/send-invites
                                      (re-fires the teamInvite email using the
                                      invite's stored token so existing links
                                      remain valid).
                                    - Real member     → POST /invite
                                      (the sendMemberInvite helper — handles
                                      the "already on team" case gracefully).

                                  Guards:
                                    - Hidden when there's no email at all
                                      (nothing to send to — the inline
                                      email-editor above collects one first).
                                    - Disabled when the coach has an unsaved
                                      email edit for a pending invite; the
                                      resend endpoint reads the DB's stored
                                      email, not the in-progress draft. ── */}
                              {/* 2026-09-17 (per Gilles) — hidden once the player HAS a
                                  Game Day account: re-inviting someone who's already in
                                  is redundant (their email is shown above). */}
                              {(member.email && !member.missingEmail && !hasAccount(member)) && (() => {
                                const hasUnsavedEmailEdit = member.isPending && emailEditing === member.inviteId;
                                const isSending =
                                  (member.isPending && sendingSingle === member.id) ||
                                  (!member.isPending && reInviting === member.id);
                                const everSent = member.isPending ? member.inviteSent : !!member.lastLogin;
                                const actionLabel = everSent ? 'Re-send Invite' : 'Send Invite';
                                const displayName = member.name || member.email;
                                return (
                                  <button
                                    onClick={async () => {
                                      if (hasUnsavedEmailEdit) {
                                        toast.error('Save the email edit first, then re-send the invite');
                                        return;
                                      }
                                      if (member.isPending) {
                                        setSendingSingle(member.id);
                                        try {
                                          await api.post(`/teams/${teamId}/import-players/send-invites`, { inviteIds: [member.inviteId] });
                                          toast.success(`Invite ${everSent ? 're-sent' : 'sent'} to ${member.email}!`);
                                          loadMembers();
                                        } catch (err) {
                                          toast.error(err.response?.data?.error || 'Failed to send invite');
                                        }
                                        setSendingSingle(null);
                                      } else {
                                        await sendMemberInvite(member);
                                      }
                                    }}
                                    disabled={isSending || hasUnsavedEmailEdit}
                                    title={hasUnsavedEmailEdit ? 'Save the email edit first, then re-send' : ''}
                                    className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
                                    style={{
                                      background: hasUnsavedEmailEdit ? 'var(--surface-2)' : 'rgba(0,212,255,0.08)',
                                      border: `1.5px solid ${hasUnsavedEmailEdit ? 'var(--border)' : 'rgba(0,212,255,0.3)'}`,
                                      color: hasUnsavedEmailEdit ? 'var(--text-3)' : 'var(--ice)',
                                      opacity: hasUnsavedEmailEdit ? 0.6 : 1,
                                    }}>
                                    {isSending
                                      ? <><div className="spinner" style={{ width: 12, height: 12 }} /> {everSent ? 'Re-sending…' : 'Sending…'}</>
                                      : <><Mail size={14} /> {hasUnsavedEmailEdit ? 'Save email first' : `${actionLabel} to ${displayName}`}</>}
                                  </button>
                                );
                              })()}

                              {/* ── Personal sign-up link (Apr 2026) ──
                                  Each player gets a unique URL the coach
                                  can share privately. For PENDING invites
                                  this surfaces the existing token URL; for
                                  REAL members it generates a fresh 30-day
                                  single-use link. Hidden for tombstoned
                                  rows (account deleted) and for staff
                                  who don't need a re-onboarding URL. ── */}
                              {/* 2026-09-17 — also hidden for account holders (nothing to sign up for). */}
                              {!member.isDeleted && !['coach','manager'].includes(member.role) && !hasAccount(member) && (
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-2)' }}>
                                    🔗 Personal sign-up link
                                  </p>
                                  {personalLinks[member.id]?.link ? (
                                    <>
                                      <div className="px-3 py-2 mb-2 rounded-lg text-xs select-all break-all"
                                        style={{ background: 'var(--surface-3)', border: '1px solid var(--border)', color: 'var(--text-2)', fontFamily: 'monospace', lineHeight: 1.5, WebkitUserSelect: 'all', userSelect: 'all' }}
                                        onClick={e => { const r = document.createRange(); r.selectNodeContents(e.currentTarget); const s = window.getSelection(); s.removeAllRanges(); s.addRange(r); }}>
                                        {personalLinks[member.id].link}
                                      </div>
                                      <div className="flex gap-2">
                                        <button onClick={() => copyPersonalLink(member.id)}
                                          className="btn-ice flex-1 justify-center py-1.5 text-xs flex items-center gap-1.5">
                                          {personalLinkCopied === member.id
                                            ? <><Check size={12} /> Copied!</>
                                            : <><Copy size={12} /> Copy Link</>}
                                        </button>
                                        <button
                                          onClick={() => {
                                            // Apr 30 2026 — labelled "Refresh" in
                                            // the UI (was "Rotate"). For pending
                                            // invites this re-fetches the same
                                            // token (no rotation server-side);
                                            // for personal links it generates a
                                            // fresh token. The behaviour is
                                            // unchanged — only the label.
                                            setPersonalLinks(prev => { const n = { ...prev }; delete n[member.id]; return n; });
                                            fetchPersonalLink(member);
                                          }}
                                          disabled={personalLinkLoading === member.id}
                                          className="btn-ghost px-3 py-1.5 text-xs flex items-center gap-1.5"
                                          title={personalLinks[member.id].type === 'personal'
                                            ? 'Generate a new 30-day link (old one stops working)'
                                            : 'Re-fetch the existing pending invite link'}>
                                          <RefreshCw size={12} />
                                          <span className="hidden sm:inline">Refresh</span>
                                        </button>
                                      </div>
                                      <p className="text-xs mt-1.5" style={{ color: 'var(--text-3)', lineHeight: 1.4 }}>
                                        {personalLinks[member.id].type === 'pending_invite'
                                          ? 'This is the original invite link. Re-share if the email never arrived.'
                                          : 'Single-use link, expires in 30 days. Share privately — the recipient signs in as this player.'}
                                      </p>
                                    </>
                                  ) : (
                                    /* Apr 30 2026 — initial "Show personal
                                       link" button restyled to match the
                                       "Send Invite to <Player>" affordance
                                       directly above (ice-tinted background,
                                       ice-coloured text). Was btn-ghost
                                       (white/grey) which made it easy to
                                       miss; coaches asked for it to "stand
                                       out" so they discover the per-player
                                       sign-up link feature. Same hover
                                       state as Send Invite for visual
                                       consistency. */
                                    <button onClick={() => fetchPersonalLink(member)}
                                      disabled={personalLinkLoading === member.id}
                                      className="w-full justify-center py-2 text-xs flex items-center gap-2 rounded-xl font-semibold"
                                      style={{
                                        background: 'rgba(0,212,255,0.08)',
                                        border: '1.5px solid rgba(0,212,255,0.3)',
                                        color: 'var(--ice)',
                                      }}>
                                      {personalLinkLoading === member.id
                                        ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Generating…</>
                                        : <><Link2 size={12} /> Show personal link</>}
                                    </button>
                                  )}
                                </div>
                              )}

                              <div className="flex gap-2 mt-3">
                                <button onClick={() => { setEditId(null); setRemoveFromSeasonConfirm(null); }} className="btn-ghost flex-1 justify-center py-2">Cancel</button>
                                <button onClick={() => saveEdit(member)} className="btn-ice flex-1 justify-center py-2">Save</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Bulk action bar ── */}
      {isCoach && selectedCount > 0 && (
        <div style={{ position: 'fixed', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)', left: 0, right: 0, zIndex: 40, display: 'flex', justifyContent: 'center', padding: '0 16px', pointerEvents: 'none' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 16, padding: '10px 14px',
            boxShadow: '0 4px 32px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,212,255,0.15)',
            maxWidth: 520, width: '100%', pointerEvents: 'all',
            flexWrap: 'wrap', rowGap: 8,
          }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ice)', flexShrink: 0 }}>{selectedCount} selected</span>
            <button onClick={clearSelection} title="Deselect all" style={{ padding: '2px 4px', background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', flexShrink: 0 }}><X size={14} /></button>
            <div style={{ flex: 1 }} />

            {/* 2026-09-17 (per Gilles) — more bulk actions: Rating, Email,
                Resend invite, Position. Each runs the same per-player call
                the edit panel uses, once per selected row. */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setBulkRatingOpen(v => !v); setBulkPosOpen(false); setBulkPickerOpen(false); }} disabled={bulkWorking}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 700, background: 'rgba(255,149,0,0.12)', border: '1px solid rgba(255,149,0,0.3)', color: 'var(--puck)', cursor: 'pointer' }}>
                ⭐ Rating <ChevronDown size={10} />
              </button>
              {bulkRatingOpen && (
                <div style={{ position: 'absolute', bottom: '100%', right: 0, zIndex: 60, marginBottom: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 12, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)', minWidth: 'min(230px, calc(100vw - 32px))' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Set rating to…</p>
                  <div style={{ display: 'grid', gap: 6 }}>
                    {RATINGS.map(r => (
                      <button key={r.value} onClick={() => bulkSetField('rating', r.value, `${r.emoji} ${r.label}`)}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, textAlign: 'left', cursor: 'pointer', background: `${r.color}15`, border: `1.5px solid ${r.color}40`, color: r.color, fontSize: 12, fontWeight: 700 }}>
                        <span>{r.emoji}</span> {r.label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setBulkRatingOpen(false)} style={{ width: '100%', marginTop: 8, padding: '5px 0', background: 'none', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                </div>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setBulkPosOpen(v => !v); setBulkRatingOpen(false); setBulkPickerOpen(false); }} disabled={bulkWorking}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 700, background: 'rgba(0,232,122,0.12)', border: '1px solid rgba(0,232,122,0.3)', color: 'var(--goal)', cursor: 'pointer' }}>
                🏒 Position <ChevronDown size={10} />
              </button>
              {bulkPosOpen && (
                <div style={{ position: 'absolute', bottom: '100%', right: 0, zIndex: 60, marginBottom: 8, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 12, boxShadow: '0 -8px 32px rgba(0,0,0,0.5)', minWidth: 'min(230px, calc(100vw - 32px))' }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 }}>Set position to…</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    {[
                      { key: 'forward', emoji: '🏒', label: 'Forward',  positions: ['forward'] },
                      { key: 'defense', emoji: '🛡️', label: 'Defence',  positions: ['defense'] },
                      { key: 'flex',    emoji: '🔀', label: 'Flex',     positions: [] },
                      { key: 'goalie',  emoji: '🥅', label: 'Goalie',   positions: ['goalie'] },
                    ].map(p => (
                      <button key={p.key} onClick={() => bulkSetPosition(p)}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 9, textAlign: 'left', cursor: 'pointer', background: 'var(--ice-a8)', border: '1.5px solid var(--ice-a30)', color: 'var(--ice)', fontSize: 12, fontWeight: 700 }}>
                        <span>{p.emoji}</span> {p.label}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setBulkPosOpen(false)} style={{ width: '100%', marginTop: 8, padding: '5px 0', background: 'none', border: '1px solid var(--border)', borderRadius: 7, color: 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                </div>
              )}
            </div>
            <button onClick={openMessagePlayers} disabled={bulkWorking}
              title="Email the selected players"
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 700, background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--ice)', cursor: 'pointer' }}>
              <Mail size={12} /> Email
            </button>
            {(() => {
              const inviteable = selectedVisible.map(id => members.find(m => m.id === id)).filter(needsInvite);
              return (
                <button onClick={() => inviteable.length ? sendInvitesTo(inviteable) : toast('Everyone selected already has a Game Day account')}
                  disabled={bulkWorking || bulkSending}
                  title={inviteable.length ? `Re-send the invite to ${inviteable.length} selected player${inviteable.length !== 1 ? 's' : ''} without an account` : 'Nobody selected needs an invite'}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 700, background: 'var(--surface-2)', border: '1px solid var(--border)', color: inviteable.length ? 'var(--text)' : 'var(--text-3)', cursor: 'pointer' }}>
                  {bulkSending ? <div className="spinner" style={{ width: 12, height: 12 }} /> : <><RefreshCw size={12} /> Resend invite{inviteable.length ? ` (${inviteable.length})` : ''}</>}
                </button>
              );
            })()}

            {/* 2026-07-10 (batch-B A7, coach request) — bulk "Season"
                assignment button REMOVED: seasons are mandatory now and
                every player-write lands in the page's season
                automatically (Phase 1 invariant). SeasonPicker component
                + bulkAddToSeason/bulkRemoveFromSeason handlers left as
                dead code per house rule. */}

            {/* Reclassify */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setBulkPickerOpen(v => !v); setSeasonPickerOpen(false); }} disabled={bulkWorking}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 700, background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: 'var(--ice)', cursor: 'pointer' }}>
                <RefreshCw size={12} /> Reclassify <ChevronDown size={10} />
              </button>
              {bulkPickerOpen && <ClassifyPicker onSelect={bulkReclassify} onClose={() => setBulkPickerOpen(false)} />}
            </div>

            {/* Delete */}
            <button onClick={bulkDelete} disabled={bulkWorking}
              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: 9, fontSize: 12, fontWeight: 700,
                background: bulkDeleteConfirm ? 'var(--penalty)' : 'rgba(255,59,59,0.12)',
                border: '1px solid rgba(255,59,59,0.3)',
                color: bulkDeleteConfirm ? '#fff' : '#ff3b3b', cursor: 'pointer' }}>
              {bulkWorking
                ? <div className="spinner" style={{ width: 12, height: 12 }} />
                : <><Trash2 size={12} /> {bulkDeleteConfirm ? 'Confirm Delete?' : 'Delete'}</>}
            </button>
          </div>
        </div>
      )}

      {/* ── 2026-09-17 (Phase 5) — Resend Invites confirmation popup ── */}
      {resendModalOpen && isCoach && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}
          onClick={e => { if (e.target === e.currentTarget && !bulkSending) setResendModalOpen(false); }}>
          <div className="glass w-full sm:max-w-md animate-slide-up"
            style={{ maxHeight: '85dvh', overflowY: 'auto', borderRadius: '16px 16px 0 0', padding: '24px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)', position: 'relative' }}>
            <ModalCloseButton onClose={() => setResendModalOpen(false)} disabled={bulkSending} />
            <h2 className="section-title text-2xl mb-1" style={{ paddingRight: 44 }}>Resend invites</h2>
            <p className="text-xs text-secondary mb-3" style={{ lineHeight: 1.5 }}>
              These players have an email on file but haven't created a Game Day account yet.
              Players who already have an account aren't included.
            </p>
            <div className="rounded-xl mb-4" style={{ border: '1px solid var(--border)', background: 'var(--surface-2)', maxHeight: 280, overflowY: 'auto' }}>
              {resendTargets.map((m, i) => (
                <div key={m.id} className="flex items-center gap-2 px-3 py-2 text-sm"
                  style={{ borderTop: i ? '1px solid var(--border)' : 'none' }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {m.name || m.email}
                    <span className="text-xs ml-1.5" style={{ color: 'var(--text-3)' }}>{m.email}</span>
                  </span>
                  <span className="text-xs flex-shrink-0" style={{ color: m.isPending ? (m.inviteSent ? 'var(--text-2)' : 'var(--puck)') : 'var(--ice)' }}>
                    {m.isPending ? (m.inviteSent ? 'invite sent' : 'never sent') : 'not joined'}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setResendModalOpen(false)} disabled={bulkSending} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="button" onClick={() => sendInvitesTo(resendTargets)} disabled={bulkSending || resendTargets.length === 0} className="btn-ice flex-1 justify-center">
                {bulkSending ? <><div className="spinner" style={{ width: 13, height: 13 }} /> Sending…</> : <><Mail size={14} /> Send to {resendTargets.length}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add player modal ── */}
      {/* ── Email Players modal ────────────────────────────────────────
          Coach/manager compose a message that emails out to players based
          on a group filter (all / full-time / spares / goalies / coaches)
          or to specific selected players. Reply-To is the coach's email,
          so replies come back to them directly. ── */}
      {msgOpen && isCoach && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}
          onClick={e => { if (e.target === e.currentTarget && !msgSending) setMsgOpen(false); }}>
          <div className="glass w-full sm:max-w-lg animate-slide-up"
            style={{
              maxHeight: '92dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              borderRadius: '16px 16px 0 0',
              padding: '24px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
              position: 'relative',
            }}>
            <ModalCloseButton onClose={() => setMsgOpen(false)} disabled={msgSending} />
            <h2 className="section-title text-2xl mb-1" style={{ paddingRight: 44 }}>Email Players</h2>
            <p className="text-xs text-secondary mb-4" style={{ lineHeight: 1.5 }}>
              Sent as an email from Game Day. Replies come directly to your inbox.
            </p>

            {/* Group selector */}
            <label className="label">Send to</label>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                { key: 'all',       label: 'Everyone',    emoji: '👥' },
                { key: 'full_time', label: 'Full-Time',   emoji: '👤' },
                { key: 'spares',    label: 'Spares',      emoji: '🔄' },
                { key: 'goalies',   label: 'Goalies',     emoji: '🥅' },
                { key: 'coaches',   label: 'Staff',       emoji: '🛡' },
                { key: 'custom',    label: 'Pick Players', emoji: '✔' },
              ].map(opt => {
                const active = msgGroup === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setMsgGroup(opt.key)}
                    disabled={msgSending}
                    className="flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-sm font-semibold transition-all"
                    style={{
                      background: active ? 'var(--ice-a12)' : 'var(--surface-2)',
                      border: `1.5px solid ${active ? 'var(--ice-a30)' : 'var(--border)'}`,
                      color: active ? 'var(--ice)' : 'var(--text-2)',
                      cursor: msgSending ? 'not-allowed' : 'pointer',
                    }}>
                    <span>{opt.emoji}</span>
                    <span>{opt.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Custom-pick selector */}
            {msgGroup === 'custom' && (
              <div className="mb-3 p-2 rounded-lg" style={{ background: 'var(--surface-2)', maxHeight: 200, overflowY: 'auto' }}>
                <div className="text-xs mb-2" style={{ color: 'var(--text-2)' }}>
                  Tap players to include. Pending invites and players without email are skipped.
                </div>
                {members
                  .filter(m => !m.isPending && m.userId && m.email)
                  .map(m => {
                    const selected = msgCustomIds.includes(m.userId);
                    return (
                      <label
                        key={m.userId}
                        className="flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer"
                        style={{
                          background: selected ? 'var(--ice-a8)' : 'transparent',
                          borderRadius: 6,
                        }}>
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            setMsgCustomIds(prev => selected
                              ? prev.filter(id => id !== m.userId)
                              : [...prev, m.userId]);
                          }}
                          disabled={msgSending}
                        />
                        <span className="text-sm flex-1 truncate" style={{ color: 'var(--text)' }}>
                          {m.name || m.email}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-3)' }}>
                          {m.role}
                        </span>
                      </label>
                    );
                  })}
                {members.filter(m => !m.isPending && m.userId && m.email).length === 0 && (
                  <p className="text-xs p-2" style={{ color: 'var(--text-3)' }}>
                    No players with email on file yet.
                  </p>
                )}
              </div>
            )}

            {/* Recipient count preview */}
            <div
              className="text-xs mb-4 px-3 py-2 rounded-lg"
              style={{
                background: previewRecipientCount > 0 ? 'var(--ice-a5)' : 'rgba(255,149,0,0.08)',
                border: `1px solid ${previewRecipientCount > 0 ? 'var(--ice-a15)' : 'rgba(255,149,0,0.2)'}`,
                color: previewRecipientCount > 0 ? 'var(--ice)' : 'var(--puck)',
              }}>
              {previewRecipientCount > 0
                ? `✉ ${previewRecipientCount} recipient${previewRecipientCount !== 1 ? 's' : ''} with email on file`
                : '⚠ No recipients match — pick another group or player list'}
            </div>

            <label className="label">Subject</label>
            <input
              type="text"
              value={msgSubject}
              onChange={e => setMsgSubject(e.target.value)}
              maxLength={200}
              placeholder="Subject line"
              className="input-field mb-3"
              disabled={msgSending}
            />

            <label className="label">Message</label>
            <textarea
              value={msgBody}
              onChange={e => setMsgBody(e.target.value)}
              maxLength={4000}
              rows={8}
              placeholder="Type your message here…"
              className="input-field mb-1"
              style={{ resize: 'vertical', minHeight: 130, fontFamily: 'inherit', lineHeight: 1.5 }}
              disabled={msgSending}
            />
            <div className="text-xs text-right mb-4" style={{ color: 'var(--text-3)', fontSize: 10 }}>
              {msgBody.length}/4000
            </div>

            {/* ── Channel selector (Apr 2026, Tom_Notes B8) ──
                Multi-select. At least one must remain checked
                (the unchecker refuses the deselection if it would
                empty the list). Email is the default for back-
                compat with the legacy per-recipient flow. */}
            <label className="label">Send via</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { key: 'email',  emoji: '📧', label: 'Email',
                  desc: 'Lands in the inbox. Replies go to your email.' },
                { key: 'push',   emoji: '📱', label: 'Push',
                  desc: 'OS notification on phones with the app installed.' },
                { key: 'in_app', emoji: '🔔', label: 'In-app',
                  desc: 'Bell-icon feed inside the app.' },
              ].map(opt => {
                const active = msgChannels.includes(opt.key);
                const toggle = () => {
                  setMsgChannels(prev => {
                    if (active && prev.length === 1) return prev;
                    return active ? prev.filter(c => c !== opt.key) : [...prev, opt.key];
                  });
                };
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={toggle}
                    disabled={msgSending}
                    className="flex flex-col items-start gap-1 py-2.5 px-3 rounded-lg text-left transition-all"
                    style={{
                      background: active ? 'var(--ice-a8)' : 'var(--surface-2)',
                      border: `1.5px solid ${active ? 'var(--ice-a40)' : 'var(--border)'}`,
                      color: active ? 'var(--ice)' : 'var(--text-2)',
                      cursor: msgSending ? 'not-allowed' : 'pointer',
                      opacity: msgSending ? 0.6 : 1,
                    }}
                    title={opt.desc}
                  >
                    <span className="text-xs font-bold">
                      {active ? '✓ ' : ''}{opt.emoji} {opt.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { if (!msgSending) setMsgOpen(false); }}
                className="btn-ghost flex-1 justify-center"
                disabled={msgSending}>
                Cancel
              </button>
              <button
                type="button"
                onClick={sendMessagePlayers}
                disabled={msgSending || !msgSubject.trim() || !msgBody.trim() || previewRecipientCount === 0}
                className="btn-ice flex-1 justify-center">
                {msgSending
                  ? <><div className="spinner" style={{ width: 14, height: 14 }} /> Sending…</>
                  : <><Mail size={14} /> Send to {previewRecipientCount}</>}
              </button>
            </div>
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}

      {showAdd && isCoach && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ zIndex: 500 }}>
          <div className="glass w-full sm:max-w-md animate-slide-up"
            style={{
              maxHeight: '92dvh', overflowY: 'auto', WebkitOverflowScrolling: 'touch',
              borderRadius: '16px 16px 0 0', padding: '24px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)',
              position: 'relative',
            }}>
            <ModalCloseButton onClose={() => { setShowAdd(false); setAddNoSeasonConfirm(false); setAddSeasonIds([]); }} />
            <h2 className="section-title text-2xl mb-5" style={{ paddingRight: 44 }}>Add Player</h2>
            <form onSubmit={addPlayer} className="space-y-4">
              <div>
                <label className="label">Name (optional)</label>
                <input type="text" className="input-field" placeholder="Player's name"
                  value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                  autoComplete="name" />
                <p className="text-xs text-secondary mt-1">They can update this once they create their account.</p>
              </div>

              <div>
                <label className="label">Email Address {addForm.noEmail ? '' : '*'}</label>
                <input
                  type="email"
                  required={!addForm.noEmail}
                  disabled={addForm.noEmail}
                  className="input-field"
                  placeholder={addForm.noEmail ? 'No email — added as a placeholder' : 'player@example.com'}
                  value={addForm.noEmail ? '' : addForm.email}
                  onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))}
                  style={addForm.noEmail ? { opacity: 0.5 } : undefined} />
                {/* v2.5.186 — "no email" toggle. When on, we skip the
                    invite entirely and add the person as a placeholder
                    roster slot (no account, no email). This replaces the
                    old "+ Slot" button by folding it into Add Player. */}
                <label className="flex items-center gap-2 mt-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={addForm.noEmail}
                    onChange={e => setAddForm(p => ({ ...p, noEmail: e.target.checked }))}
                    style={{ width: 16, height: 16, accentColor: 'var(--ice)', cursor: 'pointer' }} />
                  <span className="text-xs" style={{ color: 'var(--text-2)' }}>
                    This person has no email — add them as a placeholder
                  </span>
                </label>
                <p className="text-xs text-secondary mt-1">
                  {addForm.noEmail
                    ? 'No invite is sent. You can add their email later from their row to invite them to sign up.'
                    : "No account? They'll get an invite email to sign up and join."}
                </p>
              </div>

              <div>
                <label className="label">Classification</label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORY_DEFS.filter(c => c.key !== 'coaches').map(c => (
                    <button type="button" key={c.key}
                      onClick={() => { const cls = CLASSIFY_PUT[c.key]; setAddForm(p => ({ ...p, memberRole: cls.memberRole, isSpare: cls.isSpare, position: cls.position })); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 9, cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left',
                        background: addForm.position === c.position && addForm.isSpare === c.isSpare ? `${c.color}18` : 'var(--surface-2)',
                        border: `1.5px solid ${addForm.position === c.position && addForm.isSpare === c.isSpare ? c.color + '60' : 'var(--border)'}`,
                        color: addForm.position === c.position && addForm.isSpare === c.isSpare ? c.color : 'var(--text-2)',
                      }}>
                      <span style={{ fontSize: 16 }}>{c.emoji}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.2 }}>{c.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Initial Rating</label>
                <div className="flex gap-2">
                  {RATINGS.map(r => (
                    <button type="button" key={r.value} onClick={() => setAddForm(p => ({ ...p, rating: r.value }))}
                      className="flex-1 py-2.5 rounded-lg text-sm font-bold transition-all"
                      style={{ background: addForm.rating === r.value ? `${r.color}20` : 'var(--surface-2)', border: `1px solid ${addForm.rating === r.value ? r.color : 'transparent'}`, color: addForm.rating === r.value ? r.color : 'var(--text-secondary)' }}>
                      {r.emoji} {r.value}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label">Role</label>
                <select className="input-field" value={addForm.memberRole} onChange={e => setAddForm(p => ({ ...p, memberRole: e.target.value }))}>
                  <option value="player">Player</option>
                  <option value="coach">Coach</option>
                  {/* Apr 30 2026 (App_Change_List #1) — two-role
                      model: Coach + Player. Manager removed from the
                      picker; backend still accepts the role for
                      back-compat with legacy data. */}
                </select>
              </div>

              <div>
                <label className="label">Personal Message (optional)</label>
                <textarea className="input-field resize-none" rows={2}
                  placeholder="Hey! Come join our Monday night team…"
                  value={inviteMsg} onChange={e => setInviteMsg(e.target.value)} />
              </div>

              {/* ── Season assignment — one or more seasons this player
                  should belong to. Only rendered when the team HAS seasons
                  to assign. If none are picked we show a confirm banner
                  below before accepting the submit. ── */}
              {/* ── Schedule picker (Apr 2026) ──
                  Previous behavior was confusing: if the coach left
                  chips blank and hit Add, we toasted a warning then
                  let the second click through. Coaches routinely
                  didn't notice the warning and ended up with players
                  attached to the team but no schedule. New behavior:
                  require an explicit pick — either one+ real schedule
                  chips, OR the "No schedule — will assign later"
                  chip. The two are mutually exclusive: picking the
                  no-schedule chip clears any schedule picks, and
                  picking a real schedule clears the no-schedule chip.
                  Submit is blocked (disabled button) until something
                  is picked. ── */}
              {seasons.filter(s => s.isActive !== false).length > 0 && (
                <div>
                  <label className="label">
                    Assign to Schedule(s)
                    <span style={{ color: 'var(--penalty)', marginLeft: 4 }}>*</span>
                    {/* 2026-07-10 (Phase 3.2) — active season preselected. */}
                    <span className="text-xs" style={{ color: 'var(--text-3)', marginLeft: 8, fontWeight: 400 }}>
                      (your active season is preselected)
                    </span>
                  </label>
                  <div
                    style={{
                      display: 'flex', flexWrap: 'wrap', gap: 6,
                      padding: 8, borderRadius: 10,
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                    }}>
                    {/* Apr 2026 — archived seasons hidden from
                        new-player assignment picker so coaches don't
                        accidentally add fresh players to a closed
                        season. */}
                    {seasons.filter(s => s.isActive !== false).map(s => {
                      const picked = addSeasonIds.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            // Picking any real schedule clears the
                            // explicit no-schedule flag — the two are
                            // mutually exclusive.
                            setAddNoSeasonConfirm(false);
                            setAddSeasonIds(prev => picked
                              ? prev.filter(id => id !== s.id)
                              : [...prev, s.id]);
                          }}
                          className="text-xs font-semibold transition-all"
                          style={{
                            padding: '6px 12px', borderRadius: 999,
                            background: picked ? 'var(--ice-a15)' : 'transparent',
                            border: picked
                              ? '1.5px solid var(--ice)'
                              : '1.5px dashed var(--border)',
                            color: picked ? 'var(--ice)' : 'var(--text-secondary)',
                            cursor: 'pointer',
                          }}>
                          {picked ? '✓ ' : '+ '}{s.name}
                        </button>
                      );
                    })}
                    {/* Dedicated "no schedule" chip — makes the
                        intentional choice explicit and audit-trail-
                        able. Mutually exclusive with schedule picks. */}
                    <button
                      type="button"
                      onClick={() => {
                        setAddSeasonIds([]);
                        setAddNoSeasonConfirm(v => !v);
                      }}
                      className="text-xs font-semibold transition-all"
                      style={{
                        padding: '6px 12px', borderRadius: 999,
                        background: addNoSeasonConfirm ? 'rgba(255,149,0,0.15)' : 'transparent',
                        border: addNoSeasonConfirm
                          ? '1.5px solid var(--puck)'
                          : '1.5px dashed var(--border)',
                        color: addNoSeasonConfirm ? 'var(--puck)' : 'var(--text-secondary)',
                        cursor: 'pointer',
                      }}>
                      {addNoSeasonConfirm ? '✓ ' : '+ '}No schedule — will assign later
                    </button>
                  </div>
                  <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.5 }}>
                    Tap one or more schedules, OR pick "No schedule" to
                    add the player to the team only. Required.
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={() => { setShowAdd(false); setAddNoSeasonConfirm(false); setAddSeasonIds([]); }} className="btn-ghost justify-center" style={{flex:'0 0 auto', padding:'10px 14px'}}>Cancel</button>
                {(() => {
                  // Apr 2026: disable submit until schedule pick
                  // requirement is satisfied. Ensures the coach
                  // can't accidentally press through without a
                  // conscious schedule choice.
                  const scheduleBlocked =
                    seasons.length > 0 && addSeasonIds.length === 0 && !addNoSeasonConfirm;
                  // v2.5.186 — no-email path: one plain "Add Player"
                  // button. No invite, and the schedule gate doesn't
                  // apply (placeholders have no season membership).
                  if (addForm.noEmail) {
                    return (
                      <button type="submit"
                        disabled={!addForm.name?.trim()}
                        className="btn-ice flex-1 justify-center text-sm"
                        style={{ opacity: addForm.name?.trim() ? 1 : 0.5, cursor: addForm.name?.trim() ? 'pointer' : 'not-allowed' }}>
                        Add Player
                      </button>
                    );
                  }
                  return (
                    <>
                      <button type="button" onClick={addPlayerOnly}
                        disabled={scheduleBlocked}
                        className="btn-ghost border border-white/15 flex-1 justify-center text-sm"
                        title={scheduleBlocked ? 'Pick a schedule first' : 'Add without sending invite'}
                        style={{ opacity: scheduleBlocked ? 0.5 : 1, cursor: scheduleBlocked ? 'not-allowed' : 'pointer' }}>
                        Add Only
                      </button>
                      <button type="submit"
                        disabled={scheduleBlocked}
                        className="btn-ice flex-1 justify-center text-sm"
                        style={{ opacity: scheduleBlocked ? 0.5 : 1, cursor: scheduleBlocked ? 'not-allowed' : 'pointer' }}>
                        <Mail size={14} /> Add &amp; Invite
                      </button>
                    </>
                  );
                })()}
              </div>
              <p className="text-xs text-secondary text-center -mt-1">
                {addForm.noEmail
                  ? 'Added as a placeholder — no email or invite is sent.'
                  : '"Add Only" — no email sent. Use when you\'ll invite manually later.'}
              </p>
            </form>
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}

      {/* ── Add Placeholder modal (Apr 2026, Tom_Notes B15) ──
          Coach types a label, picks a position + optional rating,
          and the row lands in team_members with is_placeholder=TRUE.
          Counts toward roster, can be auto-balanced into teams,
          shows on game-day rosters. */}
      {showAddPlaceholder && (
        <div onClick={() => !savingPlaceholder && setShowAddPlaceholder(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
            paddingTop: 60, zIndex: 500, overflowY: 'auto',
          }}>
          <div onClick={e => e.stopPropagation()}
            className="card max-w-md w-full mx-4 mb-8"
            // Mobile Safari fix: 100vh includes the address bar and clips this
            // modal's bottom off-screen on phones. --mobile-h (set by
            // useCapacitorPlugins) tracks the real visible viewport; 100dvh is
            // the fallback for browser contexts where that var isn't set.
            style={{ maxHeight: 'calc(var(--mobile-h, 100dvh) - 80px)', overflowY: 'auto', position: 'relative' }}>
            <ModalCloseButton onClose={() => setShowAddPlaceholder(false)} disabled={savingPlaceholder} />
            <h2 className="section-title text-2xl mb-2" style={{ paddingRight: 44 }}>Add Placeholder</h2>
            <p className="text-xs text-secondary mb-5">
              A placeholder is a player slot without a real user — useful when you know
              you'll have someone but don't have their email yet, or a friend-of-a-friend
              who doesn't want a Game Day account. They count toward the player list and can
              be assigned to balanced teams.
            </p>
            <form onSubmit={addPlaceholder} className="space-y-3">
              <div>
                <label className="label">Label</label>
                <input type="text" autoFocus className="input-field"
                  placeholder="e.g. Mike's Friend, Goalie #2, TBD"
                  value={placeholderForm.name} maxLength={60}
                  onChange={e => setPlaceholderForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Position</label>
                  <select className="input-field"
                    value={placeholderForm.position}
                    onChange={e => setPlaceholderForm(f => ({ ...f, position: e.target.value }))}>
                    <option value="flex">Flex (any)</option>
                    <option value="forward">Forward</option>
                    <option value="defense">Defense</option>
                    <option value="goalie">Goalie</option>
                  </select>
                </div>
                <div>
                  <label className="label">Rating (optional)</label>
                  <select className="input-field"
                    value={placeholderForm.rating}
                    onChange={e => setPlaceholderForm(f => ({ ...f, rating: e.target.value }))}>
                    <option value="">Unrated</option>
                    <option value="1">1 — Elite</option>
                    <option value="2">2 — Strong</option>
                    <option value="3">3 — Average</option>
                    <option value="4">4 — Beginner</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox"
                  checked={placeholderForm.isSpare}
                  onChange={e => setPlaceholderForm(f => ({ ...f, isSpare: e.target.checked }))} />
                <span>Treat as a spare (not a full-timer)</span>
              </label>
              <div>
                <label className="label">Notes (optional, coaches only)</label>
                <textarea className="input-field" rows={2} maxLength={500}
                  placeholder="e.g. Plays defence usually, sometimes runs late."
                  value={placeholderForm.notes}
                  onChange={e => setPlaceholderForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowAddPlaceholder(false)}
                  className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" disabled={savingPlaceholder || !placeholderForm.name.trim()}
                  className="btn-ice flex-1 justify-center">
                  {savingPlaceholder ? 'Adding…' : 'Add Slot'}
                </button>
              </div>
            </form>
            <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
          </div>
        </div>
      )}
    </div>
  );
}
